import { useState } from "react";
import { Layout } from "@/components/Layout";
import { MetricCard } from "@/components/MetricCard";
import { MarginBarChart } from "@/components/MarginBarChart";
import {
  AlertCircle,
  PackageX,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  LayoutDashboard,
  BarChart3,
  Calendar,
  ShoppingCart,
  IndianRupee,
  Wallet,
  CreditCard,
  LineChart as LineChartIcon,
  PieChart as PieChartIcon,
  Timer,
  Package,
  Warehouse,
  DollarSign,
  CheckCircle,
  CalendarDays,
  Clock3,
  AlertTriangle,
  XCircle,
  Percent,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ComposedChart,
  Line,
  LineChart,
  ReferenceLine,
  LabelList,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  useSalesmanAnalysis,
  useSalesmanWiseMarginAnalysis,
  useDashboardOutstanding,
  useDashboardSalesPurchases,
  useDashboardSalesPurchasesWithoutTarget,
  useDashboardSummary,
  useDeadStocks,
  useMarginAnalysis,
  useManufacturerMargins,
  useCustomerMargins,
  useMarginSummaryValues,
  useMarginTrendsMonthlyPerformance,
  useCreditAgingVsMargin,
} from "@/lib/api";
import { cn } from "@/lib/utils";
import { authService } from "@/lib/api/services/auth.service";
import { AnimatedEmoji, AnimatedEmojis } from "@/components/AnimatedEmoji";

// Color map for tooltip and legend - matches the chart colors
const dataKeyColorMap: Record<string, string> = {
  previous: '#005a8f',           // Blue - Previous Year
  previousSales: '#005a8f',      // Blue - Previous Year (cumulative)
  currentActual: '#008a86',      // Teal - Current Year (Actual)
  currentSalesActual: '#008a86', // Teal - Current Year Actual (cumulative)
  currentForecast: '#f97316',    // Orange - Current Year (Forecast)
  currentSalesForecast: '#f97316', // Orange - Current Year Forecast (cumulative)
  currentYearAvgSales: '#059669', // Bold Emerald - Current Year Average Sales
  previousYearAvgSales: '#4f46e5', // Bold Indigo - Previous Year Average Sales
  current: '#008a86',            // Teal - Current (fallback)
  currentSales: '#008a86',       // Teal - Current Sales (fallback)
  deadStockPurchasesvalue: '#008a86',      // Teal - Professional
  deadStockSaleValue: '#0072B5',           // Professional Blue
  deadStockCogs: '#f97316',                // Orange - Professional
  deadStockPercentage: '#dc2626',          // Sophisticated Red
};

// Custom Tooltip Component with glassmorphism
const CustomTooltip = ({ active, payload, label, formatter }: any) => {
  // Get correct color for tooltip entries
  const getTooltipColor = (entry: any) => {
    // First check if we have a mapped color for this dataKey
    if (entry.dataKey && dataKeyColorMap[entry.dataKey]) {
      return dataKeyColorMap[entry.dataKey];
    }
    // If color is a gradient URL, use stroke color
    if (entry.color?.startsWith('url(')) {
      return entry.payload?.stroke || entry.stroke || entry.color;
    }
    return entry.color || entry.stroke;
  };

  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip min-w-[160px] sm:min-w-[200px] max-w-[90vw]">
        <p className="text-xs font-semibold text-foreground mb-1.5">{label}</p>
        {payload.map((entry: any, index: number) => {
          const displayName = entry.name === "% Dead Stock" ? "Dead Stock" : entry.name;
          return (
            <div key={index} className="flex items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-2">
                <span 
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: getTooltipColor(entry) }}
                />
                <span className="text-muted-foreground">{displayName}:</span>
              </div>
              <span className="font-medium text-foreground text-right">
                {formatter ? formatter(entry.value, entry.name, entry) : entry.value}
              </span>
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

// Custom Legend Component with pill-style badges
const CustomLegend = ({ payload }: any) => {
  // Map legend colors - use dataKey color map or stroke when fill is a gradient
  const getColor = (entry: any) => {
    // First check if we have a mapped color for this dataKey
    if (entry.dataKey && dataKeyColorMap[entry.dataKey]) {
      return dataKeyColorMap[entry.dataKey];
    }
    // If color starts with 'url(' it's a gradient, use stroke instead
    if (entry.color?.startsWith('url(')) {
      return entry.payload?.stroke || entry.color;
    }
    return entry.color;
  };

  return (
    <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mt-3 sm:mt-4">
      {payload?.map((entry: any, index: number) => (
        <div 
          key={index} 
          className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-muted/50 backdrop-blur-sm border border-border/50"
        >
          <span 
            className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full shadow-sm" 
            style={{ backgroundColor: getColor(entry) }}
          />
          <span className="text-[10px] sm:text-xs font-medium text-muted-foreground">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

// Modern stat card component for dashboard tabs
interface StatCardProps {
  title: React.ReactNode;
  value: React.ReactNode;
  icon: React.ElementType;
  variant?: "blue" | "slate" | "emerald" | "violet" | "amber" | "green" | "cyan" | "orange" | "red";
  subtitle?: string;
  trend?: {
    value: string;
    positive: boolean;
  };
  className?: string;
}

const StatCard = ({ title, value, icon: Icon, variant = "blue", subtitle, trend, className }: StatCardProps) => {
  const variants = {
    blue: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(0,114,181,0.4)]",
      iconBg: "from-[#0072B5] via-[#0090B5] to-[#00B5B0]",
      accentLine: "from-[#0072B5] via-[#0090B5] to-[#00B5B0]",
      textGradient: "from-[#0072B5] to-[#00B5B0]",
      glowColor: "rgba(0,114,181,0.15)",
    },
    slate: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(100,116,139,0.4)]",
      iconBg: "from-slate-500 via-slate-600 to-gray-700",
      accentLine: "from-slate-400 via-gray-500 to-zinc-600",
      textGradient: "from-slate-600 to-gray-700",
      glowColor: "rgba(100,116,139,0.15)",
    },
    emerald: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(16,185,129,0.4)]",
      iconBg: "from-emerald-500 via-emerald-600 to-teal-600",
      accentLine: "from-emerald-500 via-teal-500 to-cyan-500",
      textGradient: "from-emerald-500 to-teal-600",
      glowColor: "rgba(16,185,129,0.15)",
    },
    violet: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(139,92,246,0.4)]",
      iconBg: "from-violet-500 via-purple-600 to-fuchsia-600",
      accentLine: "from-violet-500 via-purple-500 to-fuchsia-500",
      textGradient: "from-violet-500 to-fuchsia-600",
      glowColor: "rgba(139,92,246,0.15)",
    },
    amber: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(245,158,11,0.4)]",
      iconBg: "from-amber-500 via-orange-500 to-orange-600",
      accentLine: "from-amber-500 via-orange-500 to-red-500",
      textGradient: "from-amber-500 to-orange-600",
      glowColor: "rgba(245,158,11,0.15)",
    },
    green: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(34,197,94,0.4)]",
      iconBg: "from-green-500 via-green-600 to-emerald-600",
      accentLine: "from-green-500 via-emerald-500 to-teal-500",
      textGradient: "from-green-500 to-emerald-600",
      glowColor: "rgba(34,197,94,0.15)",
    },
    cyan: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(0,181,176,0.4)]",
      iconBg: "from-[#00B5B0] via-[#0090B5] to-[#0072B5]",
      accentLine: "from-[#00B5B0] via-[#0090B5] to-[#0072B5]",
      textGradient: "from-[#00B5B0] to-[#0072B5]",
      glowColor: "rgba(0,181,176,0.15)",
    },
    orange: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(249,115,22,0.4)]",
      iconBg: "from-orange-500 via-orange-600 to-red-600",
      accentLine: "from-orange-500 via-red-500 to-rose-500",
      textGradient: "from-orange-500 to-red-600",
      glowColor: "rgba(249,115,22,0.15)",
    },
    red: {
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(239,68,68,0.4)]",
      iconBg: "from-red-500 via-rose-500 to-pink-600",
      accentLine: "from-red-500 via-rose-500 to-pink-500",
      textGradient: "from-red-500 to-pink-600",
      glowColor: "rgba(239,68,68,0.15)",
    },
  };

  const config = variants[variant];

  return (
    <Card className={cn(
      "group relative overflow-hidden rounded-2xl",
      "bg-gradient-to-br from-background/95 via-background/90 to-muted/50",
      "backdrop-blur-xl",
      "border border-white/10 dark:border-white/5",
      "hover:border-white/20 dark:hover:border-white/10",
      "transition-all duration-500 ease-out",
      "hover:-translate-y-1",
      config.glow,
      className
    )}>
      {/* Animated gradient border */}
      <div className={cn(
        "absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500",
        "bg-gradient-to-r p-[1px]",
        config.accentLine
      )}>
        <div className="absolute inset-[1px] rounded-2xl bg-background/95" />
      </div>
      
      {/* Background glow orbs */}
      <div 
        className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-all duration-700"
        style={{ background: `radial-gradient(circle, ${config.glowColor}, transparent 70%)` }}
      />
      <div 
        className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-0 group-hover:opacity-40 transition-all duration-700 delay-100"
        style={{ background: `radial-gradient(circle, ${config.glowColor}, transparent 70%)` }}
      />
      
      {/* Mesh pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02] group-hover:opacity-[0.04] transition-opacity duration-500" 
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: '24px 24px'
        }}
      />
      
      <div className="relative p-3 sm:p-5">
        <div className="flex items-start justify-between gap-2 sm:gap-4">
          {/* Icon with enhanced styling */}
          <div className="relative">
            {/* Icon glow */}
            <div 
              className={cn(
                "absolute inset-0 rounded-2xl blur-xl opacity-40 group-hover:opacity-70 transition-opacity duration-500",
                `bg-gradient-to-br ${config.iconBg}`
              )}
            />
            <div className={cn(
              "relative p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl",
              `bg-gradient-to-br ${config.iconBg}`,
              "shadow-lg",
              "ring-1 ring-white/25",
              "group-hover:scale-110 group-hover:rotate-[-6deg] transition-all duration-500 ease-out"
            )}>
              <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white drop-shadow-md" strokeWidth={2.5} />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 text-right">
            <p className="text-[10px] sm:text-xs font-medium text-muted-foreground/70 uppercase tracking-wider mb-0.5 sm:mb-1">
              {title}
            </p>
            {subtitle && (
              <p className="text-[9px] sm:text-[10px] text-muted-foreground/50 mb-0.5 sm:mb-1">{subtitle}</p>
            )}
            <div className={cn(
              "text-lg sm:text-2xl font-bold tracking-tight",
              "bg-gradient-to-r bg-clip-text text-transparent",
              config.textGradient
            )}>
              {value}
            </div>
            {trend && (
              <div className={cn(
                "flex items-center justify-end gap-1.5 mt-2 text-xs font-medium",
                trend.positive ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400"
              )}>
                {trend.positive 
                  ? <TrendingUp className="w-4 h-4" /> 
                  : <TrendingDown className="w-4 h-4" />
                }
                <span>{trend.value}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Bottom decorative element */}
        <div className="mt-4 flex items-center gap-2">
          <div className={cn(
            "flex-1 h-1 rounded-full bg-gradient-to-r opacity-20 group-hover:opacity-40 transition-opacity duration-500",
            config.accentLine
          )} />
          <div className={cn(
            "w-1.5 h-1.5 rounded-full bg-gradient-to-r",
            config.accentLine,
            "animate-pulse"
          )} />
        </div>
      </div>
    </Card>
  );
};

// Modern chart card wrapper
interface ChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  variant?: "default" | "blue" | "purple" | "emerald" | "red";
  className?: string;
  icon?: React.ReactNode;
  showGradientLine?: boolean;
  action?: React.ReactNode;
}

const ChartCard = ({ title, subtitle, children, variant = "default", className, icon, showGradientLine = true, action }: ChartCardProps) => {
  const variants = {
    default: {
      gradient: "from-[#0072B5] via-[#0090B5] to-[#00B5B0]",
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(0,114,181,0.25)]",
      iconBg: "from-[#0072B5]/20 via-[#0090B5]/20 to-[#00B5B0]/20",
      accent: "from-[#0072B5] to-[#00B5B0]",
    },
    blue: {
      gradient: "from-[#0072B5] via-[#0090B5] to-[#00B5B0]",
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(0,114,181,0.25)]",
      iconBg: "from-[#0072B5]/20 via-[#0090B5]/20 to-[#00B5B0]/20",
      accent: "from-[#0072B5] to-[#00B5B0]",
    },
    purple: {
      gradient: "from-purple-500 via-pink-400 to-rose-500",
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(168,85,247,0.25)]",
      iconBg: "from-purple-500/20 via-pink-400/20 to-rose-500/20",
      accent: "from-purple-500 to-rose-500",
    },
    emerald: {
      gradient: "from-emerald-500 via-teal-400 to-[#00B5B0]",
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(16,185,129,0.25)]",
      iconBg: "from-emerald-500/20 via-teal-400/20 to-[#00B5B0]/20",
      accent: "from-emerald-500 to-[#00B5B0]",
    },
    red: {
      gradient: "from-red-500 via-rose-400 to-pink-500",
      glow: "group-hover:shadow-[0_20px_50px_-12px_rgba(239,68,68,0.25)]",
      iconBg: "from-red-500/20 via-rose-400/20 to-pink-500/20",
      accent: "from-red-500 to-pink-500",
    },
  };

  const config = variants[variant];

  return (
    <Card className={cn(
      "group relative overflow-hidden rounded-2xl",
      "glass-card noise-overlay",
      "border-white/10 dark:border-white/5",
      "transition-all duration-500 ease-out",
      "hover:-translate-y-1",
      config.glow,
      className
    )}>
      {/* Animated gradient border */}
      {showGradientLine && (
        <div className={cn(
          "absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r",
          config.gradient,
          "opacity-60 group-hover:opacity-100 transition-opacity duration-500"
        )} />
      )}
      
      {/* Background glow orbs */}
      <div 
        className={cn(
          "absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-30 transition-all duration-700",
          `bg-gradient-to-br ${config.gradient}`
        )}
      />
      <div 
        className={cn(
          "absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-0 group-hover:opacity-20 transition-all duration-700 delay-100",
          `bg-gradient-to-br ${config.gradient}`
        )}
      />
      
      {/* Mesh pattern overlay */}
      <div className="absolute inset-0 opacity-[0.02] group-hover:opacity-[0.04] transition-opacity duration-500" 
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: '24px 24px'
        }}
      />
      
      <CardHeader className="relative pb-2 pt-3 sm:pt-4 px-3 sm:px-4">
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm sm:text-base font-semibold flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1">
              {icon && (
                <div className={cn(
                  "relative p-1.5 sm:p-2 rounded-lg sm:rounded-xl shrink-0",
                  `bg-gradient-to-br ${config.iconBg}`,
                  "ring-1 ring-white/10",
                  "group-hover:scale-110 group-hover:rotate-[-3deg] transition-all duration-500"
                )}>
                  <div className="relative z-10">
                    {icon}
                  </div>
                  <div className={cn(
                    "absolute inset-0 rounded-lg sm:rounded-xl blur-md opacity-0 group-hover:opacity-50 transition-opacity duration-500",
                    `bg-gradient-to-br ${config.gradient}`
                  )} />
                </div>
              )}
              <span className={cn(
                "bg-gradient-to-r bg-clip-text text-transparent truncate",
                variant === 'purple' && "from-purple-500 to-rose-500",
                variant === 'emerald' && "from-emerald-500 to-[#00B5B0]",
                (variant === 'default' || variant === 'blue') && "from-[#0072B5] to-[#00B5B0]",
                variant === 'red' && "from-red-500 to-pink-500"
              )}>
                {title}
              </span>
            </CardTitle>
            {subtitle && (
              <p className="text-[10px] sm:text-xs text-muted-foreground/80 ml-0.5 truncate">{subtitle}</p>
            )}
          </div>
          {action && (
            <div className="shrink-0 flex items-center">{action}</div>
          )}
        </div>
      </CardHeader>
      <CardContent className="relative pt-0 px-3 sm:px-4 pb-0 overflow-visible">
        {children}
      </CardContent>
      
      {/* Bottom decorative accent */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
    </Card>
  );
};

const formatINR = (n: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);

// Format values in lakhs only (no thousands/crores)
// 1,00,000 => 1.00 L, 50,00,000 => 50.00 L
const formatLakhsCurrency = (amount: number): string => {
  const value = Number.isFinite(amount) ? amount : 0;
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  const lakhs = absValue / 100000;
  return `${sign}₹${lakhs.toFixed(2)} L`;
};

// Lakhs-only formatter without currency symbol (for plain values)
const formatLakhsValue = (amount: number): string => {
  const value = Number.isFinite(amount) ? amount : 0;
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  const lakhs = absValue / 100000;
  return `${sign}${lakhs.toFixed(2)}L`;
};

// Main compact formatter for this dashboard: always show in lakhs with currency
const formatCompact = (amount: number): string => {
  return formatLakhsCurrency(amount);
};

// Tooltip values – show in lakhs with currency
const formatTooltipValue = (amount: number): string => {
  return formatLakhsCurrency(amount);
};

// Format values in compact format without currency symbol – lakhs only
// Examples: 95073.85 -> 0.95L, 165960.25 -> 1.66L, 10000000.00 -> 100.00L
const formatCompactValue = (amount: number): string => {
  return formatLakhsValue(amount);
};

// Format currency for tooltips and tables
const formatCurrency = (amount: number) => {
  return formatLakhsCurrency(amount);
};

// Month name mapping
const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];


const COLORS = ['#10b981', '#f97316', '#f97316', '#ef4444'];

// Transform API data for Manufacturer Margins table
type ManufacturerMarginRow = {
  id: number;
  name: string;
  volume: number;
  grossMargin: number;
  netMargin: number;
  schemeBenefit: number;
  trend: 'UP' | 'Down' | 'stable';
};

const transformManufacturerMargins = (data: any[]): ManufacturerMarginRow[] => {
  if (!data || data.length === 0) return [];
  
  return data.map((item, index): ManufacturerMarginRow => {
    // Calculate scheme benefit as the difference between gross and net percentage
    const schemeBenefit = item.grossPercentage - item.netPercentage;
    
    // Determine trend based on position
    const trend: 'up' | 'down' | 'stable' = 
      item.trend === 'UP' ? 'up' :
      item.trend === 'Down' ? 'down' : 'stable';
    
    return {
      id: index + 1,
      name: item.manufacturerName || '',
      volume: item.marginValue || 0,
      grossMargin: item.grossPercentage || 0,
      netMargin: item.netPercentage || 0,
      schemeBenefit: schemeBenefit,
      trend:item.trend,
    };
  });
};

// Helper function to map API customer margin data to component format
const mapCustomerMargins = (data: any[]) => {
  return data.map((item, index) => {
    // Map status from API format to component format
    const statusMap: Record<string, 'good' | 'warning' | 'critical'> = {
      'good': 'good',
      'warning': 'warning',
      'critical': 'critical',
      'Good': 'good',
      'Warning': 'warning',
      'Critical': 'critical',
    };
    const mappedStatus = statusMap[item.status] || 'good';
    
    return {
      id: index + 1,
      name: item.partyName || '',
      sales: item.salesValue || 0,
      profit: item.profitValue || 0,
      margin: item.marginPercentage || 0,
      creditDays: item.creditDays || 0,
      status: mappedStatus,
    };
  });
};

// Type colors for customer types
const typeColors: Record<string, string> = {
  'Chain': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  'Hospital': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  'Retailer': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  'Govt': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
};

const Dashboard = () => {
  // Get firmId from authenticated user
  const user = authService.getCurrentUser();
  const firmId = user?.firmId;

  // Target percentage selection for target-based forecast
  const [targetPercentage, setTargetPercentage] = useState<number>(5);
  const [targetPercentageMonthly, setTargetPercentageMonthly] = useState<number>(5);
  const targetPercentageOptions = [5, 10, 15, 20, 25, 30];

  const { data: salesmanData, isLoading: isSalesmanLoading, isError: isSalesmanError } = useSalesmanAnalysis({ firmId });
  const { data: salesmanMarginData, isLoading: isSalesmanMarginLoading, isError: isSalesmanMarginError } = useSalesmanWiseMarginAnalysis({ firmId });
  const { data: outstandingData, isLoading: isOutstandingLoading, isError: isOutstandingError } = useDashboardOutstanding({ firmId });
  const { 
    data: baseSalesPurchasesData = [], 
    isLoading: isBaseSalesPurchasesLoading, 
    isError: isBaseSalesPurchasesError 
  } = useDashboardSalesPurchasesWithoutTarget({ firmId });
  const { 
    data: salesPurchasesData = [], 
    isLoading: isSalesPurchasesLoading, 
    isFetching: isSalesPurchasesFetching, 
    isError: isSalesPurchasesError 
  } = useDashboardSalesPurchases({ firmId, targetPercentage });
  const { 
    data: salesPurchasesDataMonthly = [], 
    isLoading: isSalesPurchasesLoadingMonthly, 
    isFetching: isSalesPurchasesFetchingMonthly, 
    isError: isSalesPurchasesErrorMonthly 
  } = useDashboardSalesPurchases({ firmId, targetPercentage: targetPercentageMonthly });
  const { data: summaryData, isLoading: isSummaryLoading, isError: isSummaryError } = useDashboardSummary({ firmId });
  const {
    data: deadStocksData = [],
    isLoading: isDeadStocksLoading,
    isError: isDeadStocksError,
  } = useDeadStocks({ firmId });
  const { data: marginData, isLoading: isMarginLoading, isError: isMarginError } = useMarginAnalysis({ firmId });
  const { data: manufacturerMarginsData, isLoading: isManufacturerMarginsLoading, isError: isManufacturerMarginsError } = useManufacturerMargins({ firmId });
  const { data: customerMarginsData, isLoading: isCustomerMarginsLoading, isError: isCustomerMarginsError } = useCustomerMargins({ firmId });
  const { data: marginSummaryData, isLoading: isMarginSummaryLoading, isError: isMarginSummaryError } = useMarginSummaryValues({ firmId });
  const { data: marginTrendsData, isLoading: isMarginTrendsLoading, isError: isMarginTrendsError } = useMarginTrendsMonthlyPerformance({ firmId });
  const { data: creditAgingVsMarginData, isLoading: isCreditAgingLoading, isError: isCreditAgingError } = useCreditAgingVsMargin({ firmId });

  // Transform API data to chart format
  // Financial year: April(0) to March(11)
  // Dynamically calculate current month index based on actual date
  const now = new Date();
  const currentCalendarMonth = now.getMonth(); // 0-11 (Jan=0, Dec=11)
  // Convert calendar month to financial year index
  // April(3) -> 0, May(4) -> 1, ..., Dec(11) -> 8, Jan(0) -> 9, Feb(1) -> 10, Mar(2) -> 11
  const currentMonthIndex = currentCalendarMonth >= 3 
    ? currentCalendarMonth - 3  // April through December: subtract 3
    : currentCalendarMonth + 9; // January through March: add 9

  // Transform API data to chart format with actual vs forecast split
  const mapSalesPurchasesToChart = (data: any[]) =>
    (data || []).map((item, idx) => {
      const isActualMonth = idx <= currentMonthIndex;
      const currentSales = item.currentYearSales || 0;
      const previousSales = item.previousYearSales || 0;
      return {
        month: item.monthName || "N/A",
        current: currentSales,
        currentActual: isActualMonth ? currentSales : null,
        currentForecast: !isActualMonth ? currentSales : null,
        previous: previousSales,
        currentYearAvgSales: isActualMonth ? (item.currentYearAvgSales || 0) : null,
        previousYearAvgSales: item.previousYearAvgSales || 0,
      };
    });

  // Base chart uses data without target; target chart uses data with selected percentage
  const baseSalesData = mapSalesPurchasesToChart(baseSalesPurchasesData);
  const salesData = mapSalesPurchasesToChart(salesPurchasesData);
  const salesDataMonthly = mapSalesPurchasesToChart(salesPurchasesDataMonthly);

  // Helper function to calculate cumulative data
  const calculateCumulativeData = (data: any[]) => {
    return (data || []).map((item, idx, arr) => {
      const prevItems = arr.slice(0, idx + 1);
      const currentSalesValue = prevItems.reduce((sum, i) => sum + i.currentYearSales, 0);
      
      // Split current year sales into actual (till current month) and forecast (future months)
      // Month at currentMonthIndex should have both values for line continuity
      const isActualMonth = idx <= currentMonthIndex;
      const isForecastMonth = idx >= currentMonthIndex;
      
      return {
        month: item.monthName || "N/A",
        currentSales: currentSalesValue,
        currentSalesActual: isActualMonth ? currentSalesValue : null,
        currentSalesForecast: isForecastMonth ? currentSalesValue : null,
        previousSales: prevItems.reduce((sum, i) => sum + i.previousYearSales, 0),
        currentPurchases: prevItems.reduce((sum, i) => sum + i.currentYearPurchase, 0),
        previousPurchases: prevItems.reduce((sum, i) => sum + i.previousYearPurchase, 0),
        currentReceipts: prevItems.reduce((sum, i) => sum + i.currentYearReceipts, 0),
        previousReceipts: prevItems.reduce((sum, i) => sum + i.previousYearReceipts, 0),
        currentPayments: prevItems.reduce((sum, i) => sum + i.currentYearPayments, 0),
        previousPayments: prevItems.reduce((sum, i) => sum + i.previousYearPayments, 0),
      };
    });
  };

  // Calculate cumulative data for base chart (without target)
  const baseCumulativeData = calculateCumulativeData(baseSalesPurchasesData);
  
  // Calculate cumulative data for target chart (uses selected target percentage)
  const cumulativeData = calculateCumulativeData(salesPurchasesData);

  const hasSalesPurchasesData = salesPurchasesData.length > 0 && cumulativeData.length > 0;
  const hasSalesPurchasesDataMonthly = salesPurchasesDataMonthly.length > 0 && salesDataMonthly.length > 0;
  const hasBaseSalesPurchasesData = baseSalesPurchasesData.length > 0;

  // Calculate sum of Current Year (Forecast) - last month's cumulative forecast value
  const totalCurrentYearForecast = baseCumulativeData.length > 0 
    ? (baseCumulativeData[baseCumulativeData.length - 1]?.currentSalesForecast ?? 0)
    : 0;

  // Calculate sum of Current Year (Target) - last month's cumulative target value
  const totalCurrentYearTarget = cumulativeData.length > 0 
    ? (cumulativeData[cumulativeData.length - 1]?.currentSalesForecast ?? 0)
    : 0;

  // Calculate sum of Current Year (Forecast) for monthly chart - sum of all forecast months
  const totalMonthlyForecast = baseSalesData.reduce((sum, item) => sum + (item.currentForecast ?? 0), 0);

  // Calculate sum of Current Year (Target) for monthly chart - sum of all forecast months
  const totalMonthlyTarget = salesDataMonthly.reduce((sum, item) => sum + (item.currentForecast ?? 0), 0);

  // Calculate totals from base data (for first chart - without target)
  const baseTotalCurrentYearSales = baseSalesData.reduce((sum, m) => sum + m.current, 0);
  const baseTotalPreviousYearSales = baseSalesData.reduce((sum, m) => sum + m.previous, 0);
  
  // Calculate previous year sales "as on date" from base data
  const currentMonth = new Date().getMonth(); // 0-11
  const basePreviousYearAsOnDate = baseSalesData.slice(0, currentMonth + 1).reduce((sum, m) => sum + m.previous, 0);
  
  // Calculate growth percentage from base data
  const baseGrowthPercentage = baseTotalPreviousYearSales > 0 
    ? ((baseTotalCurrentYearSales - baseTotalPreviousYearSales) / baseTotalPreviousYearSales) * 100 
    : 0;

  // Calculate totals from dynamic data (for second chart - uses selected target percentage)
  const totalCurrentYearSales = salesData.reduce((sum, m) => sum + m.current, 0);
  const totalPreviousYearSales = salesData.reduce((sum, m) => sum + m.previous, 0);
  
  // Calculate previous year sales "as on date" from dynamic data
  const previousYearAsOnDate = salesData.slice(0, currentMonth + 1).reduce((sum, m) => sum + m.previous, 0);
  
  // Calculate growth percentage from dynamic data
  const growthPercentage = totalPreviousYearSales > 0 
    ? ((totalCurrentYearSales - totalPreviousYearSales) / totalPreviousYearSales) * 100 
    : 0;

  // Transform API response to agingData format
  const agingData = outstandingData ? [
    { name: "<15 days", value: outstandingData.lessthan15, color: "hsl(var(--chart-2))" },
    { name: "15-30 days", value: outstandingData.days15_30, color: "hsl(var(--chart-1))" },
    { name: "30-60 days", value: outstandingData.days30_60, color: "hsl(var(--chart-3))" },
    { name: "60-90 days", value: outstandingData.days60_90, color: "hsl(var(--chart-4))" },
    { name: ">90 days", value: outstandingData.greaterThan90, color: "hsl(var(--destructive))" },
  ] : [];

  // Transform margin trends API data to chart format with actual vs forecast split
  // Find the last actual month index to ensure continuity
  let lastActualMonthIndex = -1;
  if (marginTrendsData && marginTrendsData.length > 0) {
    for (let i = marginTrendsData.length - 1; i >= 0; i--) {
      if (marginTrendsData[i].finYearStatus !== "0") {
        lastActualMonthIndex = i;
        break;
      }
    }
  }
  
  const marginTrends = (marginTrendsData || []).map((item, index) => {
    const monthIndex = parseInt(item.monthName) - 1; // monthName is "1"-"12", convert to 0-11
    const monthAbbr = monthIndex >= 0 && monthIndex < 12 ? monthNames[monthIndex] : item.monthName;
    const isForecast = item.finYearStatus === "0"; // "0" means future months
    // For the last actual month, also set forecast values to ensure line continuity
    const isLastActualMonth = index === lastActualMonthIndex;
    
    return {
      month: monthAbbr,
      grossMargin: item.grossMargin || 0,
      netMargin: item.netMargin || 0,
      // Split into actual and forecast based on finYearStatus
      grossMarginActual: !isForecast ? (item.grossMargin || 0) : null,
      grossMarginForecast: isForecast || isLastActualMonth ? (item.grossMargin || 0) : null,
      netMarginActual: !isForecast ? (item.netMargin || 0) : null,
      netMarginForecast: isForecast || isLastActualMonth ? (item.netMargin || 0) : null,
    };
  });

  // Calculate domain for Y-axis based on data
  const allMarginValues = marginTrends.flatMap(m => [
    m.grossMarginActual, m.grossMarginForecast, 
    m.netMarginActual, m.netMarginForecast
  ]).filter(v => v !== null && v !== undefined && !isNaN(v));
  const minMargin = allMarginValues.length > 0 ? Math.min(...allMarginValues) : 0;
  const maxMargin = allMarginValues.length > 0 ? Math.max(...allMarginValues) : 1000000;
  const marginPadding = allMarginValues.length > 0 ? (maxMargin - minMargin) * 0.1 : 100000; // 10% padding or default 100k
  const yAxisDomain = allMarginValues.length > 0 
    ? [Math.max(0, minMargin - marginPadding), maxMargin + marginPadding]
    : [0, 1000000]; // Default domain if no data

  // Transform credit aging vs margin API data to chart format
  const creditAgingData = (() => {
    if (!creditAgingVsMarginData || creditAgingVsMarginData.length === 0) {
      return [];
    }

    // Find the three objects by position
    const valueData = creditAgingVsMarginData.find(item => item.position === "Value");
    const percentageData = creditAgingVsMarginData.find(item => item.position === "Percentage");
    const customerCountData = creditAgingVsMarginData.find(item => item.position === "CustomerCount");

    // If we don't have all three, return default data
    if (!valueData || !percentageData || !customerCountData) {
      return [];
    }

    // Transform to chart format
    return [
      {
        bucket: "0-30 days",
        outstanding: valueData.lessthan30 || 0,
        margin: percentageData.lessthan30 || 0,
        customers: Math.round(customerCountData.lessthan30 || 0),
      },
      {
        bucket: "31-60 days",
        outstanding: valueData.days31_60 || 0,
        margin: percentageData.days31_60 || 0,
        customers: Math.round(customerCountData.days31_60 || 0),
      },
      {
        bucket: "61-90 days",
        outstanding: valueData.days61_90 || 0,
        margin: percentageData.days61_90 || 0,
        customers: Math.round(customerCountData.days61_90 || 0),
      },
      {
        bucket: "90+ days",
        outstanding: valueData.greaterThan90 || 0,
        margin: percentageData.greaterThan90 || 0,
        customers: Math.round(customerCountData.greaterThan90 || 0),
      },
    ];
  })();

  // Calculate appropriate scale for Y-axis based on max outstanding value
  const maxOutstanding = creditAgingData.length > 0 
    ? Math.max(...creditAgingData.map(item => item.outstanding || 0))
    : 0;
  
  // Determine the best scale and formatter for Y-axis
  const getYAxisFormatter = () => {
    if (maxOutstanding >= 10000000) {
      // Crores
      return (value: number) => `${(value / 10000000).toFixed(1)} Cr`;
    } else if (maxOutstanding >= 100000) {
      // Lakhs
      return (value: number) => `${(value / 100000).toFixed(1)} L`;
    } else if (maxOutstanding >= 1000) {
      // Thousands
      return (value: number) => `${(value / 1000).toFixed(1)} K`;
    } else {
      // Raw value
      return (value: number) => value.toFixed(0);
    }
  };

  const leftYAxisFormatter = getYAxisFormatter();

  return (
    <Layout>
      <div className="space-y-2 sm:space-y-1">
        <div className="flex items-center gap-2 px-1 sm:px-0">
          <h2 className="text-2xl sm:text-3xl font-bold">Dashboard</h2>
        {/*<AnimatedEmoji emoji="📊" size={32} className="hidden sm:inline-block" /> */}
        </div>
       
        <Tabs defaultValue="dashboard" className="w-full">
          <div className="relative pb-2 sm:pb-4">
            {/* Subtle gradient background */}
            <div className="absolute inset-0 -bottom-2 bg-gradient-to-b from-transparent via-muted/20 to-transparent pointer-events-none" />
            
            <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
              <TabsList className={cn(
                "flex w-full h-auto min-w-max sm:min-w-0",
                "bg-gradient-to-br from-background/80 via-muted/30 to-background/60",
                "backdrop-blur-xl",
                "border border-white/20 dark:border-white/10",
                "rounded-2xl p-1 sm:p-1.5",
                "shadow-lg shadow-black/5 dark:shadow-black/20",
                "gap-1"
              )}>
              <TabsTrigger 
                value="dashboard" 
                className={cn(
                  "group relative gap-1.5 sm:gap-2.5 px-2.5 sm:px-4 py-2 sm:py-2.5",
                  "rounded-xl transition-all duration-300 ease-out",
                  "text-xs sm:text-sm font-medium text-muted-foreground",
                  "hover:text-foreground hover:bg-muted/50",
                  "data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#0072B5] data-[state=active]:via-[#0090B5] data-[state=active]:to-[#00B5B0]",
                  "data-[state=active]:text-white data-[state=active]:shadow-lg",
                  "data-[state=active]:shadow-[#0072B5]/25",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072B5]/50 focus-visible:ring-offset-2",
                  "overflow-hidden shrink-0"
                )}
              >
                {/* Active state glow effect */}
                <div className="modern-tab-glow" />
                
                {/* Icon with enhanced styling */}
                <LayoutDashboard className="modern-tab-icon group-hover:scale-105 h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.5} />
                <span className="hidden sm:inline relative z-10">Dashboard</span>
                
                {/* Bottom accent line for active state */}
                <div className="modern-tab-accent" />
              </TabsTrigger>
              
              <TabsTrigger 
                value="sales" 
                className={cn(
                  "group relative gap-1.5 sm:gap-2.5 px-2.5 sm:px-4 py-2 sm:py-2.5",
                  "rounded-xl transition-all duration-300 ease-out",
                  "text-xs sm:text-sm font-medium text-muted-foreground",
                  "hover:text-foreground hover:bg-muted/50",
                  "data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#0072B5] data-[state=active]:via-[#0090B5] data-[state=active]:to-[#00B5B0]",
                  "data-[state=active]:text-white data-[state=active]:shadow-lg",
                  "data-[state=active]:shadow-[#0072B5]/25",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072B5]/50 focus-visible:ring-offset-2",
                  "overflow-hidden shrink-0"
                )}
              >
                <div className="modern-tab-glow" />
                <BarChart3 className="modern-tab-icon group-hover:scale-105 h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.5} />
                <span className="hidden sm:inline relative z-10">Sales</span>
                <div className="modern-tab-accent" />
              </TabsTrigger>
              
              <TabsTrigger 
                value="outstanding" 
                className={cn(
                  "group relative gap-1.5 sm:gap-2.5 px-2.5 sm:px-4 py-2 sm:py-2.5",
                  "rounded-xl transition-all duration-300 ease-out",
                  "text-xs sm:text-sm font-medium text-muted-foreground",
                  "hover:text-foreground hover:bg-muted/50",
                  "data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#0072B5] data-[state=active]:via-[#0090B5] data-[state=active]:to-[#00B5B0]",
                  "data-[state=active]:text-white data-[state=active]:shadow-lg",
                  "data-[state=active]:shadow-[#0072B5]/25",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072B5]/50 focus-visible:ring-offset-2",
                  "overflow-hidden shrink-0"
                )}
              >
                <div className="modern-tab-glow" />
                <AlertCircle className="modern-tab-icon group-hover:scale-105 h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.5} />
                <span className="hidden sm:inline relative z-10">Outstanding</span>
                <div className="modern-tab-accent" />
              </TabsTrigger>
              
              <TabsTrigger 
                value="expires" 
                className={cn(
                  "group relative gap-1.5 sm:gap-2.5 px-2.5 sm:px-4 py-2 sm:py-2.5",
                  "rounded-xl transition-all duration-300 ease-out",
                  "text-xs sm:text-sm font-medium text-muted-foreground",
                  "hover:text-foreground hover:bg-muted/50",
                  "data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#0072B5] data-[state=active]:via-[#0090B5] data-[state=active]:to-[#00B5B0]",
                  "data-[state=active]:text-white data-[state=active]:shadow-lg",
                  "data-[state=active]:shadow-[#0072B5]/25",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072B5]/50 focus-visible:ring-offset-2",
                  "overflow-hidden shrink-0"
                )}
              >
                <div className="modern-tab-glow" />
                <Package className="modern-tab-icon group-hover:scale-105 h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.5} />
                <span className="hidden sm:inline relative z-10">Inventory</span>
                <div className="modern-tab-accent" />
              </TabsTrigger>
              {/* Margin Analysis */}
              <TabsTrigger 
                value="marginAnalysis" 
                className={cn(
                  "group relative gap-1.5 sm:gap-2.5 px-2.5 sm:px-4 py-2 sm:py-2.5",
                  "rounded-xl transition-all duration-300 ease-out",
                  "text-xs sm:text-sm font-medium text-muted-foreground",
                  "hover:text-foreground hover:bg-muted/50",
                  "data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#0072B5] data-[state=active]:via-[#0090B5] data-[state=active]:to-[#00B5B0]",
                  "data-[state=active]:text-white data-[state=active]:shadow-lg",
                  "data-[state=active]:shadow-[#0072B5]/25",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072B5]/50 focus-visible:ring-offset-2",
                  "overflow-hidden shrink-0"
                )}
              >
                <div className="modern-tab-glow" />
                <Percent className="modern-tab-icon group-hover:scale-105 h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.5} />
                <span className="hidden sm:inline relative z-10">Margin</span>
                <div className="modern-tab-accent" />
              </TabsTrigger>
              {/* Sales Team */}
              <TabsTrigger 
                value="salesman" 
                className={cn(
                  "group relative gap-1.5 sm:gap-2.5 px-2.5 sm:px-4 py-2 sm:py-2.5",
                  "rounded-xl transition-all duration-300 ease-out",
                  "text-xs sm:text-sm font-medium text-muted-foreground",
                  "hover:text-foreground hover:bg-muted/50",
                  "data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#0072B5] data-[state=active]:via-[#0090B5] data-[state=active]:to-[#00B5B0]",
                  "data-[state=active]:text-white data-[state=active]:shadow-lg",
                  "data-[state=active]:shadow-[#0072B5]/25",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072B5]/50 focus-visible:ring-offset-2",
                  "overflow-hidden shrink-0"
                )}
              >
                <div className="modern-tab-glow" />
                <Users className="modern-tab-icon group-hover:scale-105 h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.5} />
                <span className="hidden sm:inline relative z-10">Sales Team</span>
                <div className="modern-tab-accent" />
              </TabsTrigger>
            </TabsList>
            </div>
          </div>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-4 sm:space-y-6">
            {isSummaryLoading && (
              <div className="text-center py-8 text-muted-foreground">Loading dashboard data...</div>
            )}
            {isSummaryError && !isSummaryLoading && (
              <div className="text-center py-8 text-destructive">Failed to load dashboard data</div>
            )}
            {!isSummaryLoading && !isSummaryError && summaryData && (
              <>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <MetricCard
                    title="Sales - Year to Date"
                    value={formatCompact(summaryData.sales_YearToDate)}
                    icon={IndianRupee}
                    trend={{
                      value: `${(summaryData.yearPercentage ?? 0) >= 0 ? '+' : ''}${(summaryData.yearPercentage ?? 0).toFixed(1)}% from last year`,
                      positive: (summaryData.yearPercentage ?? 0) >= 0
                    }}
                    variant="accent"
                    className="animate-fade-up stagger-1"
                  />
                  <MetricCard
                    title="Sales - Month to Date"
                    value={formatCompact(summaryData.sales_MonthToDate)}
                    icon={Calendar}
                    trend={{
                      value: `${(summaryData.monthPercentage ?? 0) >= 0 ? '+' : ''}${(summaryData.monthPercentage ?? 0).toFixed(1)}% vs same period last year`,
                      positive: (summaryData.monthPercentage ?? 0) >= 0
                    }}
                    variant="accent"
                    className="animate-fade-up stagger-2"
                  />
                  <MetricCard
                    title="Sales - Yesterday"
                    value={formatCompact(summaryData.sales_Yesterday)}
                    icon={Wallet}
                    trend={{
                      value: `${(summaryData.dailyAvgSalesPercentage ?? 0) >= 0 ? '+' : ''}${(summaryData.dailyAvgSalesPercentage ?? 0).toFixed(1)}% vs daily avg`,
                      positive: (summaryData.dailyAvgSalesPercentage ?? 0) >= 0
                    }}
                    variant="default"
                    className="animate-fade-up stagger-3"
                  />
                  <MetricCard
                    title="Outstanding - As on Date"
                    value={formatCompact(summaryData.outstandingAsOnDate)}
                    icon={CreditCard}
                    trend={{
                      value: `${(summaryData.outstandingPercentage ?? 0) >= 0 ? '+' : ''}${(summaryData.outstandingPercentage ?? 0).toFixed(1)}% vs monthly avg`,
                      positive: (summaryData.outstandingPercentage ?? 0) >= 0
                    }}
                    variant="warning"
                    className="animate-fade-up stagger-4"
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <MetricCard
                    title="Purchases - YEAR TO DATE"
                    value={formatCompact(summaryData.totalPurchasesCurrentYearValue)}
                    icon={Package}
                    trend={{
                      value: `${(summaryData.purchasesCurrentYear_Percentage ?? 0) >= 0 ? '+' : ''}${(summaryData.purchasesCurrentYear_Percentage ?? 0).toFixed(1)}% from last year`,
                      positive: (summaryData.purchasesCurrentYear_Percentage ?? 0) >= 0
                    }}
                    variant="accent"
                    className="animate-fade-up stagger-3"
                  />
                  <MetricCard
                    title="Closing Stock Value"
                    value={formatCompact(summaryData.totalClosingStockValue)}
                    icon={Warehouse}
                    trend={{
                      value: `${(summaryData.monthlyAvgSalesPercentage ?? 0) >= 0 ? '+' : ''}${(summaryData.monthlyAvgSalesPercentage ?? 0).toFixed(1)}% vs monthly avg`,
                      positive: (summaryData.monthlyAvgSalesPercentage ?? 0) >= 0
                    }}
                    variant="default"
                    className="animate-fade-up stagger-4"
                  />
                  <MetricCard
                    title="Expired Stock Value"
                    value={formatCompact(summaryData.expiredItemsValue)}
                    icon={PackageX}
                    trend={{
                      value: `${(summaryData.expiryPercentage ?? 0) >= 0 ? '+' : ''}${(summaryData.expiryPercentage ?? 0).toFixed(1)}% of inventory`,
                      positive: (summaryData.expiryPercentage ?? 0) >= 0
                    }}
                    variant="destructive"
                    className="animate-fade-up stagger-1"
                  />
                  <MetricCard
                    title="To Be Expired in 30 Days"
                    value={formatCompact(summaryData.toBeExpiredValue)}
                    icon={Clock}
                    trend={{
                      value: `${(summaryData.toBeExpiredin30DaysPercentageofInventory ?? 0) >= 0 ? '+' : ''}${(summaryData.toBeExpiredin30DaysPercentageofInventory ?? 0).toFixed(1)}% of inventory`,
                      positive: (summaryData.toBeExpiredin30DaysPercentageofInventory ?? 0) >= 0
                    }}
                    variant="warning"
                    className="animate-fade-up stagger-2"
                  />
                 
                </div>

                {/* Area Charts Side by Side */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Cumulative Sales Forecast Chart - Uses data without target */}
                  {!isBaseSalesPurchasesError && hasBaseSalesPurchasesData && (
                    <ChartCard 
                      title="Sales Comparison for Forecast" 
                      subtitle="Previous vs Current Financial Year"
                      variant="blue"
                      showGradientLine={false}
                      icon={<LineChartIcon className="h-4 w-4 text-[#0072B5]" />}
                      action={
                        <div className="flex items-center gap-2">
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-br from-orange-50/80 to-orange-100/60 dark:from-orange-950/30 dark:to-orange-900/20 border border-orange-200/50 dark:border-orange-800/30 shadow-sm backdrop-blur-sm">
                            <span className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">Forecast</span>
                            <span className="text-sm sm:text-base font-bold text-[#f97316]">{formatCompact(totalCurrentYearForecast)}</span>
                          </div>
                        </div>
                      }
                    >
                    <div className="w-full h-[280px] sm:h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={baseCumulativeData}>
                        <defs>
                          <linearGradient id="baseCumulativeActualGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#008a86" stopOpacity={0.1} />
                            <stop offset="100%" stopColor="#008a86" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="baseCumulativeForecastGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#f97316" stopOpacity={0.1} />
                            <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="baseCumulativePreviousGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#0072B5" stopOpacity={0} />
                            <stop offset="100%" stopColor="#0072B5" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                          axisLine={{ stroke: 'hsl(var(--border))' }}
                          tickLine={false}
                        />
                        <YAxis 
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                          axisLine={{ stroke: 'hsl(var(--border))' }}
                          tickLine={false}
                          tickFormatter={(value) => formatCompact(value)}
                          width={60}
                        />
                        <Tooltip content={({ active, payload, label }: any) => {
                          if (!active || !payload || !payload.length) return null;
                          
                          // Find the index of the current month in baseCumulativeData
                          const monthIndex = baseCumulativeData.findIndex((item: any) => item.month === label);
                          const isCurrentMonth = monthIndex === currentMonthIndex;
                          
                          // Filter out currentSalesForecast if it's the current month
                          const filteredPayload = isCurrentMonth 
                            ? payload.filter((entry: any) => entry.dataKey !== 'currentSalesForecast')
                            : payload;
                          
                          return (
                            <div className="chart-tooltip min-w-[160px] sm:min-w-[200px] max-w-[90vw]">
                              <p className="text-xs font-semibold text-foreground mb-1.5">{label}</p>
                              {filteredPayload.map((entry: any, index: number) => {
                                const getTooltipColor = (entry: any) => {
                                  if (entry.dataKey && dataKeyColorMap[entry.dataKey]) {
                                    return dataKeyColorMap[entry.dataKey];
                                  }
                                  if (entry.color?.startsWith('url(')) {
                                    return entry.payload?.stroke || entry.stroke || entry.color;
                                  }
                                  return entry.color || entry.stroke;
                                };
                                
                                return (
                                  <div key={index} className="flex items-center justify-between gap-4 text-xs">
                                    <div className="flex items-center gap-2">
                                      <span 
                                        className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                                        style={{ backgroundColor: getTooltipColor(entry) }}
                                      />
                                      <span className="text-muted-foreground">{entry.name}:</span>
                                    </div>
                                    <span className="font-medium text-foreground text-right">
                                      {formatCompact(entry.value)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }} />
                        <Legend content={<CustomLegend />} />
                        <Area 
                          type="monotone" 
                          dataKey="previousSales" 
                          stroke="#005a8f" 
                          strokeWidth={2.5}
                          fill="url(#baseCumulativePreviousGradient)" 
                          name="Previous Year"
                          dot={{ r: 5, fill: '#005a8f', strokeWidth: 0 }}
                          activeDot={{ r: 7, fill: '#005a8f', strokeWidth: 0 }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="currentSalesActual" 
                          stroke="#008a86" 
                          strokeWidth={2.5}
                          fill="url(#baseCumulativeActualGradient)" 
                          name="Current Year (Actual)"
                          dot={{ r: 5, fill: '#008a86', strokeWidth: 0 }}
                          activeDot={{ r: 7, fill: '#008a86', strokeWidth: 0 }}
                          connectNulls={false}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="currentSalesForecast" 
                          stroke="#f97316" 
                          strokeWidth={2.5}
                          strokeDasharray="6 4"
                          fill="url(#baseCumulativeForecastGradient)" 
                          name="Current Year (Forecast)"
                          dot={{ r: 5, fill: '#f97316', strokeWidth: 0 }}
                          activeDot={{ r: 7, fill: '#f97316', strokeWidth: 0 }}
                          connectNulls={false}
                        />
                      </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    </ChartCard>
                  )}
                  
                  {/* Sales Target Based Forecast Chart - Uses dynamic target percentage */}
                  {!isSalesPurchasesError && hasSalesPurchasesData && (
                    <ChartCard 
                      title="Sales Comparison for Target" 
                      subtitle="Target vs Forecast"
                      variant="blue"
                      showGradientLine={false}
                      icon={<LineChartIcon className="h-4 w-4 text-[#0072B5]" />}
                      action={
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-br from-orange-50/80 to-orange-100/60 dark:from-orange-950/30 dark:to-orange-900/20 border border-orange-200/50 dark:border-orange-800/30 shadow-sm backdrop-blur-sm">
                          <span className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">Target</span>
                          <span className="text-sm sm:text-base font-bold text-[#f97316]">{formatCompact(totalCurrentYearTarget)}</span>
                        </div>
                        <span className="text-[11px] text-muted-foreground hidden sm:inline">Target %</span>
                        <Select
                          value={String(targetPercentage)}
                          onValueChange={(value) => setTargetPercentage(Number(value))}
                        >
                          <SelectTrigger className="w-[80px] sm:w-[90px] h-9 text-xs">
                            <SelectValue placeholder="Target %" />
                          </SelectTrigger>
                          <SelectContent>
                            {targetPercentageOptions.map((option) => (
                              <SelectItem key={option} value={option.toString()}>
                                {option === 5 ? "5%" : `${option}%`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {isSalesPurchasesFetching && (
                          <span className="text-[11px] text-muted-foreground">Updating…</span>
                        )}
                      </div>
                    }
                  >
                    <div className="w-full h-[280px] sm:h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={cumulativeData}>
                        <defs>
                          <linearGradient id="targetCumulativeActualGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#008a86" stopOpacity={0.1} />
                            <stop offset="100%" stopColor="#008a86" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="targetCumulativeForecastGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#f97316" stopOpacity={0.1} />
                            <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="targetCumulativePreviousGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#0072B5" stopOpacity={0} />
                            <stop offset="100%" stopColor="#0072B5" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                          axisLine={{ stroke: 'hsl(var(--border))' }}
                          tickLine={false}
                        />
                        <YAxis 
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                          axisLine={{ stroke: 'hsl(var(--border))' }}
                          tickLine={false}
                          tickFormatter={(value) => formatCompact(value)}
                          width={60}
                        />
                        <Tooltip content={({ active, payload, label }: any) => {
                          if (!active || !payload || !payload.length) return null;
                          
                          // Find the index of the current month in cumulativeData
                          const monthIndex = cumulativeData.findIndex((item: any) => item.month === label);
                          const isCurrentMonth = monthIndex === currentMonthIndex;
                          
                          // Filter out currentSalesForecast if it's the current month
                          const filteredPayload = isCurrentMonth 
                            ? payload.filter((entry: any) => entry.dataKey !== 'currentSalesForecast')
                            : payload;
                          
                          return (
                            <div className="chart-tooltip min-w-[160px] sm:min-w-[200px] max-w-[90vw]">
                              <p className="text-xs font-semibold text-foreground mb-1.5">{label}</p>
                              {filteredPayload.map((entry: any, index: number) => {
                                const getTooltipColor = (entry: any) => {
                                  if (entry.dataKey && dataKeyColorMap[entry.dataKey]) {
                                    return dataKeyColorMap[entry.dataKey];
                                  }
                                  if (entry.color?.startsWith('url(')) {
                                    return entry.payload?.stroke || entry.stroke || entry.color;
                                  }
                                  return entry.color || entry.stroke;
                                };
                                
                                return (
                                  <div key={index} className="flex items-center justify-between gap-4 text-xs">
                                    <div className="flex items-center gap-2">
                                      <span 
                                        className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                                        style={{ backgroundColor: getTooltipColor(entry) }}
                                      />
                                      <span className="text-muted-foreground">{entry.name}:</span>
                                    </div>
                                    <span className="font-medium text-foreground text-right">
                                      {formatCompact(entry.value)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }} />
                        <Legend content={<CustomLegend />} />
                        <Area 
                          type="monotone" 
                          dataKey="previousSales" 
                          stroke="#005a8f" 
                          strokeWidth={2.5}
                          fill="url(#targetCumulativePreviousGradient)" 
                          name="Previous Year"
                          dot={{ r: 5, fill: '#005a8f', strokeWidth: 0 }}
                          activeDot={{ r: 7, fill: '#005a8f', strokeWidth: 0 }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="currentSalesActual" 
                          stroke="#008a86" 
                          strokeWidth={2.5}
                          fill="url(#targetCumulativeActualGradient)" 
                          name="Current Year (Actual)"
                          dot={{ r: 5, fill: '#008a86', strokeWidth: 0 }}
                          activeDot={{ r: 7, fill: '#008a86', strokeWidth: 0 }}
                          connectNulls={false}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="currentSalesForecast" 
                          stroke="#f97316" 
                          strokeWidth={2.5}
                          strokeDasharray="6 4"
                          fill="url(#targetCumulativeForecastGradient)" 
                          name="Current Year (Target)"
                          dot={{ r: 5, fill: '#f97316', strokeWidth: 0 }}
                          activeDot={{ r: 7, fill: '#f97316', strokeWidth: 0 }}
                          connectNulls={false}
                        />
                      </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    </ChartCard>
                  )}
                </div>
                
              </>
            )}
          </TabsContent>

          {/* Monthly Sales Analysis Tab */}
          <TabsContent value="sales" className="space-y-4 sm:space-y-6">
            {(isSalesPurchasesLoading || isBaseSalesPurchasesLoading) && (
              <div className="text-center py-8 text-muted-foreground">Loading sales data...</div>
            )}
            {(isSalesPurchasesError || isBaseSalesPurchasesError) && !(isSalesPurchasesLoading || isBaseSalesPurchasesLoading) && (
              <div className="text-center py-8 text-destructive">Failed to load sales data</div>
            )}
            {!(isSalesPurchasesLoading || isBaseSalesPurchasesLoading) && !(isSalesPurchasesError || isBaseSalesPurchasesError) && salesPurchasesData && baseSalesPurchasesData && (
              <>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <StatCard
                    title="Total Sales"
                    subtitle="Current Year"
                    value={formatCompact(summaryData?.sales_YearToDate ?? baseTotalCurrentYearSales)}
                    icon={IndianRupee}
                    variant="blue"
                    className="animate-fade-up stagger-1"
                  />
                  <StatCard
                    title="Total Sales"
                    subtitle="Previous Year (Till Date)"
                    value={formatCompact(summaryData?.sales_LastYearToDate ?? basePreviousYearAsOnDate)}
                    icon={Calendar}
                    variant="slate"
                    className="animate-fade-up stagger-2"
                  />
                  <StatCard
                    title="Growth Rate (%)"
                    subtitle="vs Previous Year "
                    value={
                      <span className={(summaryData?.yearPercentage ?? baseGrowthPercentage) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}>
                        {(summaryData?.yearPercentage ?? baseGrowthPercentage) >= 0 ? '+' : ''}{(summaryData?.yearPercentage ?? baseGrowthPercentage).toFixed(1)}%
                      </span>
                    }
                    icon={TrendingUp}
                    variant="emerald"
                    className="animate-fade-up stagger-3"
                  />
                  <StatCard
                    title="Total Sales"
                    subtitle="Previous Year"
                    value={formatCompact(summaryData?.sales_LastYear ?? baseTotalPreviousYearSales)}
                    icon={BarChart3}
                    variant="violet"
                    className="animate-fade-up stagger-4"
                  />
                </div>
                
                {/* Charts Side by Side */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* Monthly Sales Comparison Chart - Uses data without target */}
                  {hasBaseSalesPurchasesData && (
                    <ChartCard 
                      title="Monthly Sales Comparison for Forecast" 
                      subtitle="Previous vs Current Financial Year"
                      variant="blue"
                      showGradientLine={false}
                      icon={<BarChart3 className="h-4 w-4 text-[#0072B5]" />}
                      action={
                        <div className="flex items-center gap-2">
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-br from-orange-50/80 to-orange-100/60 dark:from-orange-950/30 dark:to-orange-900/20 border border-orange-200/50 dark:border-orange-800/30 shadow-sm backdrop-blur-sm">
                            <span className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">Forecast</span>
                            <span className="text-sm sm:text-base font-bold text-[#f97316]">{formatCompact(totalMonthlyForecast)}</span>
                          </div>
                        </div>
                      }
                    >
                    <div className="w-full h-[280px] sm:h-[350px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={baseSalesData} barGap={8}>
                      <defs>
                        <linearGradient id="previousYearBarGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0090B5" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#005a8f" stopOpacity={0.4} />
                        </linearGradient>
                        <linearGradient id="currentActualBarGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#00c4bf" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#008a86" stopOpacity={0.4} />
                        </linearGradient>
                        <linearGradient id="currentForecastBarGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#fdba74" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#f97316" stopOpacity={0.4} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        axisLine={{ stroke: 'hsl(var(--border))' }}
                        tickLine={false}
                      />
                      <YAxis 
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        axisLine={{ stroke: 'hsl(var(--border))' }}
                        tickLine={false}
                        tickFormatter={(value) => formatCompact(value)}
                        width={80}
                      />
                      <Tooltip content={({ active, payload, label }: any) => {
                        if (!active || !payload || !payload.length) return null;
                        
                        // Find the index of the current month in baseSalesData
                        const monthIndex = baseSalesData.findIndex((item: any) => item.month === label);
                        const isCurrentMonth = monthIndex === currentMonthIndex;
                        
                        // Filter out currentForecast if it's the current month
                        const filteredPayload = isCurrentMonth 
                          ? payload.filter((entry: any) => entry.dataKey !== 'currentForecast')
                          : payload;
                        
                        return (
                          <div className="chart-tooltip min-w-[160px] sm:min-w-[200px] max-w-[90vw]">
                            <p className="text-xs font-semibold text-foreground mb-1.5">{label}</p>
                            {filteredPayload.map((entry: any, index: number) => {
                              const getTooltipColor = (entry: any) => {
                                if (entry.dataKey && dataKeyColorMap[entry.dataKey]) {
                                  return dataKeyColorMap[entry.dataKey];
                                }
                                if (entry.color?.startsWith('url(')) {
                                  return entry.payload?.stroke || entry.stroke || entry.color;
                                }
                                return entry.color || entry.stroke;
                              };
                              
                              return (
                                <div key={index} className="flex items-center justify-between gap-4 text-xs">
                                  <div className="flex items-center gap-2">
                                    <span 
                                      className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                                      style={{ backgroundColor: getTooltipColor(entry) }}
                                    />
                                    <span className="text-muted-foreground">{entry.name}:</span>
                                  </div>
                                  <span className="font-medium text-foreground text-right">
                                    {formatCompact(entry.value)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }} />
                      <Legend content={<CustomLegend />} />
                      <Bar 
                        dataKey="previous" 
                        fill="url(#previousYearBarGradient)" 
                        stroke="#005a8f"
                        strokeWidth={0.5}
                        name="Previous Year" 
                        radius={[6, 6, 0, 0]}
                        legendType="circle"
                      />
                      <Bar 
                        dataKey="currentActual" 
                        fill="url(#currentActualBarGradient)" 
                        stroke="#008a86"
                        strokeWidth={0.5}
                        name="Current Year (Actual)" 
                        radius={[6, 6, 0, 0]}
                        stackId="current"
                        legendType="circle"
                      />
                      <Bar 
                        dataKey="currentForecast" 
                        fill="url(#currentForecastBarGradient)" 
                        stroke="#f97316"
                        strokeWidth={0.5}
                        name="Current Year (Forecast)" 
                        radius={[6, 6, 0, 0]}
                        stackId="current"
                        legendType="circle"
                      />
                      <Line 
                        type="monotone"
                        dataKey="currentYearAvgSales"
                        stroke="#059669"
                        strokeWidth={3.5}
                        name="Current Year Avg Sales"
                        dot={{ r: 5, fill: '#059669', strokeWidth: 2, stroke: '#ffffff' }}
                        activeDot={{ r: 7, fill: '#059669', strokeWidth: 2, stroke: '#ffffff' }}
                        legendType="circle"
                        connectNulls={false}
                      />
                      <Line 
                        type="monotone"
                        dataKey="previousYearAvgSales"
                        stroke="#4f46e5"
                        strokeWidth={3.5}
                        name="Previous Year Avg Sales"
                        dot={{ r: 5, fill: '#4f46e5', strokeWidth: 2, stroke: '#ffffff' }}
                        activeDot={{ r: 7, fill: '#4f46e5', strokeWidth: 2, stroke: '#ffffff' }}
                        legendType="circle"
                        />
                      </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                    </ChartCard>
                  )}
                  
                  {/* Sales Comparison under Target Forecast - Uses dynamic target percentage */}
                  {!isSalesPurchasesErrorMonthly && hasSalesPurchasesDataMonthly && (
                    <ChartCard 
                      title="Monthly Sales Comparison for Target" 
                      subtitle="Previous vs Current Financial Year"
                      variant="blue"
                      showGradientLine={false}
                      icon={<BarChart3 className="h-4 w-4 text-[#0072B5]" />}
                      action={
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-br from-orange-50/80 to-orange-100/60 dark:from-orange-950/30 dark:to-orange-900/20 border border-orange-200/50 dark:border-orange-800/30 shadow-sm backdrop-blur-sm">
                        <span className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wide">Target</span>
                        <span className="text-sm sm:text-base font-bold text-[#f97316]">{formatCompact(totalMonthlyTarget)}</span>
                      </div>
                      <span className="text-[11px] text-muted-foreground hidden sm:inline">Target %</span>
                      <Select
                        value={String(targetPercentageMonthly)}
                        onValueChange={(value) => setTargetPercentageMonthly(Number(value))}
                      >
                        <SelectTrigger className="w-[80px] sm:w-[90px] h-9 text-xs">
                          <SelectValue placeholder="Target %" />
                        </SelectTrigger>
                        <SelectContent>
                          {targetPercentageOptions.map((option) => (
                            <SelectItem key={option} value={option.toString()}>
                              {option === 5 ? "5%" : `${option}%`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {isSalesPurchasesFetchingMonthly && (
                        <span className="text-[11px] text-muted-foreground">Updating…</span>
                      )}
                    </div>
                  }
                >
                  <div className="w-full h-[280px] sm:h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={salesDataMonthly} barGap={8}>
                        <defs>
                          <linearGradient id="targetPreviousYearBarGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#0090B5" stopOpacity={0.5} />
                            <stop offset="100%" stopColor="#005a8f" stopOpacity={0.4} />
                          </linearGradient>
                          <linearGradient id="targetCurrentActualBarGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#00c4bf" stopOpacity={0.5} />
                            <stop offset="100%" stopColor="#008a86" stopOpacity={0.4} />
                          </linearGradient>
                          <linearGradient id="targetCurrentForecastBarGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#fdba74" stopOpacity={0.5} />
                            <stop offset="100%" stopColor="#f97316" stopOpacity={0.4} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          axisLine={{ stroke: 'hsl(var(--border))' }}
                          tickLine={false}
                        />
                        <YAxis 
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          axisLine={{ stroke: 'hsl(var(--border))' }}
                          tickLine={false}
                          tickFormatter={(value) => formatCompact(value)}
                          width={80}
                        />
                        <Tooltip content={({ active, payload, label }: any) => {
                          if (!active || !payload || !payload.length) return null;
                          
                          const monthIndex = salesDataMonthly.findIndex((item: any) => item.month === label);
                          const isCurrentMonth = monthIndex === currentMonthIndex;
                          
                          const filteredPayload = isCurrentMonth 
                            ? payload.filter((entry: any) => entry.dataKey !== 'currentForecast')
                            : payload;
                          
                          return (
                            <div className="chart-tooltip min-w-[160px] sm:min-w-[200px] max-w-[90vw]">
                              <p className="text-xs font-semibold text-foreground mb-1.5">{label}</p>
                              {filteredPayload.map((entry: any, index: number) => {
                                const getTooltipColor = (entry: any) => {
                                  if (entry.dataKey && dataKeyColorMap[entry.dataKey]) {
                                    return dataKeyColorMap[entry.dataKey];
                                  }
                                  if (entry.color?.startsWith('url(')) {
                                    return entry.payload?.stroke || entry.stroke || entry.color;
                                  }
                                  return entry.color || entry.stroke;
                                };
                                
                                return (
                                  <div key={index} className="flex items-center justify-between gap-4 text-xs">
                                    <div className="flex items-center gap-2">
                                      <span 
                                        className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                                        style={{ backgroundColor: getTooltipColor(entry) }}
                                      />
                                      <span className="text-muted-foreground">{entry.name}:</span>
                                    </div>
                                    <span className="font-medium text-foreground text-right">
                                      {formatCompact(entry.value)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }} />
                        <Legend content={<CustomLegend />} />
                        <Bar 
                          dataKey="previous" 
                          fill="url(#targetPreviousYearBarGradient)" 
                          stroke="#005a8f"
                          strokeWidth={0.5}
                          name="Previous Year" 
                          radius={[6, 6, 0, 0]}
                          legendType="circle"
                        />
                        <Bar 
                          dataKey="currentActual" 
                          fill="url(#targetCurrentActualBarGradient)" 
                          stroke="#008a86"
                          strokeWidth={0.5}
                          name="Current Year (Actual)" 
                          radius={[6, 6, 0, 0]}
                          stackId="current"
                          legendType="circle"
                        />
                        <Bar 
                          dataKey="currentForecast" 
                          fill="url(#targetCurrentForecastBarGradient)" 
                          stroke="#f97316"
                          strokeWidth={0.5}
                          name="Current Year (Target)" 
                          radius={[6, 6, 0, 0]}
                          stackId="current"
                          legendType="circle"
                        />
                        <Line 
                          type="monotone"
                          dataKey="currentYearAvgSales"
                          stroke="#059669"
                          strokeWidth={3.5}
                          name="Current Year Avg Sales"
                          dot={{ r: 5, fill: '#059669', strokeWidth: 2, stroke: '#ffffff' }}
                          activeDot={{ r: 7, fill: '#059669', strokeWidth: 2, stroke: '#ffffff' }}
                          legendType="circle"
                          connectNulls={false}
                        />
                        <Line 
                          type="monotone"
                          dataKey="previousYearAvgSales"
                          stroke="#4f46e5"
                          strokeWidth={3.5}
                          name="Previous Year Avg Sales"
                          dot={{ r: 5, fill: '#4f46e5', strokeWidth: 2, stroke: '#ffffff' }}
                          activeDot={{ r: 7, fill: '#4f46e5', strokeWidth: 2, stroke: '#ffffff' }}
                          legendType="circle"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                    </ChartCard>
                  )}
                </div>
              </>
              
            )}
          </TabsContent>

          {/* Outstanding Analysis Tab */}
          <TabsContent value="outstanding" className="space-y-4 sm:space-y-6">
            {isOutstandingLoading && (
              <div className="text-center py-8 text-muted-foreground">Loading outstanding data...</div>
            )}
            {isOutstandingError && !isOutstandingLoading && (
              <div className="text-center py-8 text-destructive">Failed to load outstanding data</div>
            )}
            {!isOutstandingLoading && !isOutstandingError && outstandingData && (
              <>
                <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  <StatCard
                    title="Total Outstanding"
                    value={formatCompact(outstandingData.outstandingAmount)}
                    icon={AlertCircle}
                    variant="amber"
                    className="col-span-1 sm:col-span-2 lg:col-span-1 animate-fade-up stagger-1"
                  />
                  {agingData.map((bucket, idx) => {
                    const variantMap: Array<"green" | "cyan" | "amber" | "orange" | "red"> = ["green", "cyan", "amber", "orange", "red"];
                    const iconMap = [CheckCircle, CalendarDays, Clock3, AlertTriangle, XCircle];
                    return (
                      <StatCard
                        key={idx}
                        title={bucket.name}
                        value={formatCompact(bucket.value)}
                        icon={iconMap[idx]}
                        variant={variantMap[idx % variantMap.length]}
                        className={`animate-fade-up stagger-${idx + 2}`}
                      />
                    );
                  })}
                </div>

                <div className="overflow-hidden">
                  <ChartCard 
                    title="Outstanding Ageing Distribution"
                    subtitle={`Total Outstanding: ${formatCompact(outstandingData.outstandingAmount)}`}
                    variant="purple"
                    showGradientLine={false}
                    icon={<PieChartIcon className="h-4 w-4 text-purple-500" />}
                  >
                    <div className="flex flex-col items-center gap-4 sm:gap-6 p-4 sm:p-8">
                      {/* Premium Donut Chart with Animated Center */}
                      <div className="relative group/chart w-full max-w-[280px] sm:max-w-[380px] aspect-square mx-auto" style={{ zIndex: 1 }}>
                        {/* Outer Glow Ring */}
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-violet-500/20 via-purple-500/20 to-fuchsia-500/20 blur-2xl opacity-0 group-hover/chart:opacity-100 transition-opacity duration-700" />
                        
                        {/* Decorative Rotating Rings - contained within bounds */}
                        <div className="absolute inset-0 rounded-full border border-dashed border-violet-500/20 animate-[spin_25s_linear_infinite]" />
                        <div className="absolute inset-0 rounded-full border border-dotted border-purple-500/10 animate-[spin_35s_linear_infinite_reverse]" />
                        
                        <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <defs>
                            {agingData.map((entry, index) => (
                              <linearGradient key={`pie-gradient-${index}`} id={`pieGradient${index}`} x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor={entry.color} stopOpacity={1} />
                                <stop offset="50%" stopColor={entry.color} stopOpacity={0.85} />
                                <stop offset="100%" stopColor={entry.color} stopOpacity={0.7} />
                              </linearGradient>
                            ))}
                            {agingData.map((entry, index) => (
                              <filter key={`glow-${index}`} id={`glow${index}`} x="-50%" y="-50%" width="200%" height="200%">
                                <feGaussianBlur stdDeviation="4" result="blur" />
                                <feFlood floodColor={entry.color} floodOpacity="0.4" result="color" />
                                <feComposite in="color" in2="blur" operator="in" result="shadow" />
                                <feMerge>
                                  <feMergeNode in="shadow" />
                                  <feMergeNode in="SourceGraphic" />
                                </feMerge>
                              </filter>
                            ))}
                          </defs>
                          <Pie
                            data={agingData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius="70%"
                            innerRadius="45%"
                            fill="#8884d8"
                            dataKey="value"
                            paddingAngle={4}
                            stroke="hsl(var(--background))"
                            strokeWidth={2}
                            animationBegin={0}
                            animationDuration={1400}
                            animationEasing="ease-out"
                            cornerRadius={6}
                          >
                            {agingData.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={`url(#pieGradient${index})`}
                                style={{ filter: `url(#glow${index})` }}
                                className="transition-all duration-500 hover:brightness-110 cursor-pointer origin-center hover:scale-[1.02]"
                              />
                            ))}
                          </Pie>
                          <Tooltip 
                            content={<CustomTooltip formatter={(v: number) => formatTooltipValue(v)} />}
                            allowEscapeViewBox={{ x: true, y: true }}
                            wrapperStyle={{ outline: 'none', zIndex: 1000 }}
                            cursor={{ fill: 'transparent' }}
                          />
                        </PieChart>
                        </ResponsiveContainer>
                      
                      {/* Premium Center Label with Glass Effect */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="relative">
                          {/* Animated pulse ring */}
                          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-fuchsia-500/10 animate-pulse" />
                          
                          {/* Glass container */}
                          <div className="relative backdrop-blur-md bg-background/80 rounded-full p-4 sm:p-8 border border-white/10 shadow-2xl">
                            <div className="text-center">
                              <p className="text-[9px] sm:text-[11px] font-semibold text-violet-500 dark:text-violet-400 uppercase tracking-[0.2em] mb-0.5 sm:mb-1">Total Due</p>
                              <p className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 bg-clip-text text-transparent">
                                {formatCompact(outstandingData.outstandingAmount)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Compact Legend Cards */}
                    <div className="w-full">
                      <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                        {agingData.map((entry, index) => {
                          const total = agingData.reduce((sum, item) => sum + item.value, 0);
                          const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0';
                          const percentNum = parseFloat(percentage);
                          return (
                            <div 
                              key={index}
                              className="group relative overflow-hidden flex items-center gap-1.5 sm:gap-2.5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl bg-gradient-to-br from-background/80 to-muted/30 backdrop-blur-sm border border-white/10 dark:border-white/5 hover:border-white/20 dark:hover:border-white/10 transition-all duration-500 cursor-pointer hover:shadow-md hover:shadow-black/5 dark:hover:shadow-black/20 hover:-translate-y-0.5"
                              style={{
                                animationDelay: `${index * 100}ms`
                              }}
                            >
                              {/* Hover gradient overlay */}
                              <div 
                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                style={{
                                  background: `linear-gradient(135deg, ${entry.color}08 0%, transparent 50%, ${entry.color}05 100%)`
                                }}
                              />
                              
                              {/* Progress bar background */}
                              <div 
                                className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r transition-all duration-700 group-hover:h-[3px]"
                                style={{ 
                                  width: `${percentNum}%`,
                                  background: `linear-gradient(90deg, ${entry.color}80, ${entry.color})`
                                }}
                              />
                              
                              {/* Color indicator */}
                              <div className="relative flex-shrink-0">
                                <div 
                                  className="absolute inset-0 rounded-lg blur-sm opacity-40 group-hover:opacity-60 transition-opacity duration-300"
                                  style={{ backgroundColor: entry.color }}
                                />
                                <div 
                                  className="relative w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-105"
                                  style={{ 
                                    background: `linear-gradient(135deg, ${entry.color} 0%, ${entry.color}CC 100%)`,
                                    boxShadow: `0 2px 8px -2px ${entry.color}50`
                                  }}
                                >
                                  <span className="text-white text-[8px] sm:text-[9px] font-bold">{Math.round(percentNum)}%</span>
                                </div>
                              </div>
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0 relative z-10">
                                <p className="text-[10px] sm:text-xs font-medium text-foreground truncate">
                                  {entry.name}
                                </p>
                                <p 
                                  className="text-[10px] sm:text-[11px] font-semibold transition-colors duration-300"
                                  style={{ color: entry.color }}
                                >
                                  {formatCompact(entry.value)}
                                </p>
                              </div>
                              
                              {/* Decorative corner accent */}
                              <div 
                                className="absolute top-0 right-0 w-10 h-10 opacity-5 group-hover:opacity-10 transition-opacity duration-300"
                                style={{
                                  background: `radial-gradient(circle at top right, ${entry.color}, transparent 70%)`
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </ChartCard>
                </div>
              </>
            )}
          </TabsContent>

          {/* Inventory Tab */}
          <TabsContent value="expires" className="space-y-4 sm:space-y-6">
            {isSummaryLoading && (
              <div className="text-center py-8 text-muted-foreground">Loading expires data...</div>
            )}
            {isSummaryError && !isSummaryLoading && (
              <div className="text-center py-8 text-destructive">Failed to load expires data</div>
            )}
            {!isSummaryLoading && !isSummaryError && summaryData && (
              <>
                <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                <MetricCard
                    title="Closing Stock Value"
                    value={formatCompact(summaryData.totalClosingStockValue)}
                    icon={Warehouse}
                    trend={{
                      value: `${(summaryData.monthlyAvgSalesPercentage ?? 0) >= 0 ? '+' : ''}${(summaryData.monthlyAvgSalesPercentage ?? 0).toFixed(1)}% vs monthly avg`,
                      positive: (summaryData.monthlyAvgSalesPercentage ?? 0) >= 0
                    }}
                    variant="default"
                    className="animate-fade-up stagger-4"
                  />
                  <StatCard
                    title="Expired Stock Value"
                    value={
                      <div className="flex flex-col items-start">
                        <span className="self-end">{formatCompact(summaryData.expiredItemsValue ?? 0)}</span>
                        <span className="text-xs sm:text-sm font-normal opacity-80 mt-1.5 !text-red-500 dark:!text-red-400 text-left" style={{ WebkitTextFillColor: 'rgb(239 68 68)' }}>
                          {summaryData.totalExpiredItems ?? 0} items
                        </span>
                      </div>
                    }
                    icon={IndianRupee}
                    variant="red"
                    className="animate-fade-up stagger-1"
                  />
                  <StatCard
                    title="VALUE EXPIRING IN 30 DAYS"
                    value={
                      <div className="flex flex-col items-start">
                        <span className="self-end">{formatCompact(summaryData.toBeExpiredValue ?? 0)}</span>
                        <span className="text-xs sm:text-sm font-normal opacity-80 mt-1.5 !text-amber-500 dark:!text-amber-400 text-left" style={{ WebkitTextFillColor: 'rgb(245 158 11)' }}>
                          {summaryData.expiringIn30Days ?? 0} items
                        </span>
                      </div>
                    }
                    icon={Clock}
                    variant="amber"
                    className="animate-fade-up stagger-2"
                  />
                  <StatCard
                    title="Dead Stock Value"
                    subtitle="."
                    value={formatCompact(summaryData.deadstockvalue ?? 0)}
                    icon={Warehouse}
                    variant="red"
                    className="animate-fade-up stagger-3"
                  />
                </div>
                
                <ChartCard
                  title="Dead Stock Analysis (>90 Days)"
                  variant="purple"
                  showGradientLine={false}
                  icon={<PackageX className="h-4 w-4 text-rose-500" />}
                >
                  {isDeadStocksLoading && (
                    <div className="py-8 text-center text-xs sm:text-sm text-muted-foreground">
                      Loading dead stock data...
                    </div>
                  )}
                  {isDeadStocksError && !isDeadStocksLoading && (
                    <div className="py-8 text-center text-xs sm:text-sm text-destructive">
                      Failed to load dead stock data
                    </div>
                  )}
                  {!isDeadStocksLoading && !isDeadStocksError && deadStocksData && deadStocksData.length > 0 && (
                    <div className="grid gap-4 md:grid-cols-2">
                      {/* Dead Stock Table */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground text-center">
                          Dead Stock by Range
                        </p>
                        <div className="rounded-xl border bg-card/60 backdrop-blur-sm">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-[10px] sm:text-[11px] py-1">Range</TableHead>
                                <TableHead className="text-[10px] sm:text-[11px] text-right py-1">Dead Stock Value (₹)</TableHead>
                                <TableHead className="text-[10px] sm:text-[11px] text-right py-1">Purchase Value (₹)</TableHead>
                                <TableHead className="text-[10px] sm:text-[11px] text-right py-1">Sale Value (₹)</TableHead>
                                <TableHead className="text-[10px] sm:text-[11px] text-right py-1">COGS (₹)</TableHead>
                                <TableHead className="text-[10px] sm:text-[11px] text-right py-1">Dead Stock (%)</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {deadStocksData.map((row, idx) => (
                                <TableRow key={idx} className="hover:bg-muted/40">
                                  <TableCell className="text-[10px] sm:text-[11px] font-medium py-1 px-2">
                                    {row.deadStockRangePercentages}
                                  </TableCell>
                                  
                                  <TableCell className="text-[10px] sm:text-[11px] text-right py-1 px-2">
                                    {formatCurrency(row.deadStockvalue ?? 0)}
                                  </TableCell>
                                  <TableCell className="text-[10px] sm:text-[11px] text-right py-1 px-2">
                                    {formatCurrency(row.deadStockPurchasesvalue ?? 0)}
                                  </TableCell>
                                  <TableCell className="text-[10px] sm:text-[11px] text-right py-1 px-2">
                                    {formatCurrency(row.deadStockSaleValue ?? 0)}
                                  </TableCell>
                                  <TableCell className="text-[10px] sm:text-[11px] text-right py-1 px-2">
                                    {formatCurrency(row.deadStockCogs ?? 0)}
                                  </TableCell>
                                  <TableCell className="text-[10px] sm:text-[11px] text-right py-1 px-2">
                                    {row.deadStockPercentage?.toFixed(2)}%
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                            <TableFooter>
                              <TableRow className="bg-slate-200 dark:bg-slate-800 font-semibold border-t-2 border-slate-300 dark:border-slate-700">
                                <TableCell className="text-[10px] sm:text-[11px] font-semibold py-1 px-2 text-slate-900 dark:text-slate-100">
                                  Total
                                </TableCell>
                                <TableCell className="text-[10px] sm:text-[11px] text-right font-semibold py-1 px-2 text-slate-900 dark:text-slate-100">
                                  {formatCurrency(
                                    deadStocksData.reduce((sum, row) => sum + (row.deadStockvalue ?? 0), 0)
                                  )}
                                </TableCell>
                                <TableCell className="text-[10px] sm:text-[11px] text-right font-semibold py-1 px-2 text-slate-900 dark:text-slate-100">
                                  {formatCurrency(
                                    deadStocksData.reduce((sum, row) => sum + (row.deadStockPurchasesvalue ?? 0), 0)
                                  )}
                                </TableCell>
                                <TableCell className="text-[10px] sm:text-[11px] text-right font-semibold py-1 px-2 text-slate-900 dark:text-slate-100">
                                  {formatCurrency(
                                    deadStocksData.reduce((sum, row) => sum + (row.deadStockSaleValue ?? 0), 0)
                                  )}
                                </TableCell>
                                <TableCell className="text-[10px] sm:text-[11px] text-right font-semibold py-1 px-2 text-slate-900 dark:text-slate-100">
                                  {formatCurrency(
                                    deadStocksData.reduce((sum, row) => sum + (row.deadStockCogs ?? 0), 0)
                                  )}
                                </TableCell>
                                <TableCell className="text-[10px] sm:text-[11px] text-right font-semibold py-1 px-2 text-slate-900 dark:text-slate-100">
                                  {(() => {
                                    const totalDeadStock = deadStocksData.reduce((sum, row) => sum + (row.deadStockvalue ?? 0), 0);
                                    const totalPurchases = deadStocksData.reduce((sum, row) => sum + (row.deadStockPurchasesvalue ?? 0), 0);
                                    const totalPercentage = totalPurchases > 0 ? (totalDeadStock / totalPurchases) * 100 : 0;
                                    return `${totalPercentage.toFixed(2)}%`;
                                  })()}
                                </TableCell>
                              </TableRow>
                            </TableFooter>
                          </Table>
                        </div>
                      </div>

                      {/* Stacked Column Chart */}
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground text-center">
                          Dead Stock Composition (₹ & %)
                        </p>
                        <div className="w-full h-[180px] sm:h-[200px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={deadStocksData} barGap={8}>
                              <defs>
                                {/* Purchase Value Gradient - Teal */}
                                <linearGradient id="deadStockPurchaseGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="hsl(var(--chart-2-light))" stopOpacity={1} />
                                  <stop offset="100%" stopColor="hsl(var(--chart-2))" stopOpacity={0.8} />
                                </linearGradient>
                                {/* Sale Value Gradient - Blue */}
                                <linearGradient id="deadStockSaleGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="hsl(var(--chart-1-light))" stopOpacity={1} />
                                  <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.8} />
                                </linearGradient>
                                {/* COGS Gradient - Orange */}
                                <linearGradient id="deadStockCogsGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#fdba74" stopOpacity={0.9} />
                                  <stop offset="50%" stopColor="#f97316" stopOpacity={0.85} />
                                  <stop offset="100%" stopColor="#ea580c" stopOpacity={0.8} />
                                </linearGradient>
                                {/* Dead Stock Percentage Line Gradient - Red */}
                                <linearGradient id="deadStockLineGradient" x1="0" y1="0" x2="1" y2="0">
                                  <stop offset="0%" stopColor="#f87171" stopOpacity={1} />
                                  <stop offset="50%" stopColor="#dc2626" stopOpacity={1} />
                                  <stop offset="100%" stopColor="#b91c1c" stopOpacity={1} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="hsl(var(--border))"
                                strokeOpacity={0.5}
                              />
                              <XAxis
                                dataKey="deadStockRangePercentages"
                                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                                axisLine={{ stroke: "hsl(var(--border))" }}
                                tickLine={false}
                              />
                              {/* Left Y axis for ₹ values */}
                              <YAxis
                                yAxisId="left"
                                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                                axisLine={{ stroke: "hsl(var(--border))" }}
                                tickLine={false}
                                tickFormatter={(value) => formatCompact(value)}
                                width={70}
                              />
                              {/* Right Y axis for percentage */}
                              <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                                axisLine={{ stroke: "hsl(var(--border))" }}
                                tickLine={false}
                                tickFormatter={(value) => `${value.toFixed(0)}%`}
                                width={50}
                              />
                              <Tooltip
                                content={
                                  <CustomTooltip
                                    formatter={(v: number, name: string) =>
                                      name === "% Dead Stock" ? `${v.toFixed(2)}%` : formatCurrency(v ?? 0)
                                    }
                                  />
                                }
                              />
                              <Legend content={<CustomLegend />} />
                              {/* Purchase Value as separate bar */}
                              <Bar
                                yAxisId="left"
                                dataKey="deadStockPurchasesvalue"
                                name="Purchase Value"
                                fill="url(#deadStockPurchaseGradient)"
                              />
                              {/* Stacked bars for Sale Value and COGS */}
                              <Bar
                                yAxisId="left"
                                dataKey="deadStockSaleValue"
                                name="Sale Value"
                                stackId="saleAndCogs"
                                fill="url(#deadStockSaleGradient)"
                              />
                              <Bar
                                yAxisId="left"
                                dataKey="deadStockCogs"
                                name="COGS"
                                stackId="saleAndCogs"
                                fill="url(#deadStockCogsGradient)"
                              />
                              {/* Line for percentage severity */}
                              <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="deadStockPercentage"
                                name="% Dead Stock"
                                stroke="url(#deadStockLineGradient)"
                                strokeWidth={2}
                                dot={{ r: 3, fill: "#dc2626" }}
                                activeDot={{ r: 4, fill: "#dc2626" }}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  )}
                </ChartCard>

                {/* Original Stock Expiry Overview charts, now shown below Dead Stock Analysis */}
                <ChartCard
                  title="Stock Expiry Overview"
                  variant="red"
                  showGradientLine={false}
                  icon={<Timer className="h-4 w-4 text-red-500" />}
                >
                  <div className="grid gap-3 md:grid-cols-2">
                    {/* Items Count Chart */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2 text-center">Items Count</p>
                      <div className="w-full h-[160px] sm:h-[180px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={[
                              {
                                category: "Expired",
                                count: summaryData.totalExpiredItems ?? 0,
                              },
                              {
                                category: "Expiring Soon (30 Days)",
                                count: summaryData.expiringIn30Days ?? 0,
                              },
                            ]}
                            barGap={8}
                          >
                            <defs>
                              <linearGradient id="itemsGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop
                                  offset="0%"
                                  stopColor="hsl(var(--chart-1-light))"
                                  stopOpacity={1}
                                />
                                <stop
                                  offset="100%"
                                  stopColor="hsl(var(--chart-1))"
                                  stopOpacity={0.8}
                                />
                              </linearGradient>
                            </defs>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="hsl(var(--border))"
                              strokeOpacity={0.5}
                            />
                            <XAxis
                              dataKey="category"
                              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 9 }}
                              axisLine={{ stroke: "hsl(var(--border))" }}
                              tickLine={false}
                            />
                            <YAxis
                              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 9 }}
                              axisLine={{ stroke: "hsl(var(--border))" }}
                              tickLine={false}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar
                              dataKey="count"
                              fill="url(#itemsGradient)"
                              name="Items"
                              radius={[6, 6, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    {/* Value Chart */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2 text-center">
                        Value (₹)
                      </p>
                      <div className="w-full h-[160px] sm:h-[180px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={[
                              {
                                category: "Expired",
                                value: summaryData.expiredItemsValue ?? 0,
                              },
                              {
                                category: "Expiring Soon (30 Days)",
                                value: summaryData.toBeExpiredValue ?? 0,
                              },
                            ]}
                            barGap={8}
                          >
                            <defs>
                              <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop
                                  offset="0%"
                                  stopColor="hsl(var(--chart-2-light))"
                                  stopOpacity={1}
                                />
                                <stop
                                  offset="100%"
                                  stopColor="hsl(var(--chart-2))"
                                  stopOpacity={0.8}
                                />
                              </linearGradient>
                            </defs>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="hsl(var(--border))"
                              strokeOpacity={0.5}
                            />
                            <XAxis
                              dataKey="category"
                              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                              axisLine={{ stroke: "hsl(var(--border))" }}
                              tickLine={false}
                            />
                            <YAxis
                              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                              axisLine={{ stroke: "hsl(var(--border))" }}
                              tickLine={false}
                              tickFormatter={(value) => formatCompact(value)}
                              width={80}
                            />
                            <Tooltip
                              content={
                                <CustomTooltip formatter={(v: number) => formatCompact(v)} />
                              }
                            />
                            <Bar
                              dataKey="value"
                              fill="url(#valueGradient)"
                              name="Value"
                              radius={[6, 6, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </ChartCard>
              </>
            )}
          </TabsContent>
          
            {/* Margin Analysis Tab */}
          <TabsContent value="marginAnalysis" className="space-y-4 sm:space-y-6">
            {isMarginLoading && (
              <div className="text-center py-8 text-muted-foreground">Loading margin analysis data...</div>
            )}
            {isMarginError && !isMarginLoading && (
              <div className="text-center py-8 text-destructive">Failed to load margin analysis data</div>
            )}
            {!isMarginLoading && !isMarginError && marginData && (
              <>
                {/* Calculate summary statistics and prepare chart data */}
                {(() => {
                  const summaryStats = {
                    totalMarginValue: marginData.reduce((sum, item) => sum + item.marginValue, 0),
                    totalGrossMargin: marginData.reduce((sum, item) => sum + item.totalGrossMargin, 0),
                    avgPercentage: marginData.reduce((sum, item) => sum + item.percentage, 0) / marginData.length,
                    totalProducts: marginData.length,
                  };

                  // Filter for Mnf margin type and group by position
                  const mnfData = marginData.filter((item) => item.marginType === "Mnf");
                  
                  // Group by position
                  const groupedByPosition: Record<string, typeof mnfData> = {};
                  mnfData.forEach((item) => {
                    const position = item.position || "Unknown";
                    if (!groupedByPosition[position]) {
                      groupedByPosition[position] = [];
                    }
                    groupedByPosition[position].push(item);
                  });

                  // Prepare chart data - group manufacturers by position
                  const chartData = Object.keys(groupedByPosition)
                    .sort((a, b) => {
                      // Sort: Top first, then Bottom
                      if (a === "Top") return -1;
                      if (b === "Top") return 1;
                      return a.localeCompare(b);
                    })
                    .map((position) => {
                      const manufacturers = groupedByPosition[position];
                      // Group by manufacturer to aggregate values
                      const manufacturerMap: Record<string, {
                        manufacturerName: string;
                        manufacturerCode: string;
                        marginValue: number;
                        percentage: number;
                        position: string;
                        count: number;
                      }> = {};

                      manufacturers.forEach((item) => {
                        const key = `${item.manufacturerCode}`;
                        if (!manufacturerMap[key]) {
                          manufacturerMap[key] = {
                            manufacturerName: item.manufacturerName,
                            manufacturerCode: item.manufacturerCode,
                            marginValue: 0,
                            percentage: 0,
                            position: item.position,
                            count: 0,
                          };
                        }
                        manufacturerMap[key].marginValue += item.marginValue;
                        manufacturerMap[key].percentage += item.percentage;
                        manufacturerMap[key].count += 1;
                      });

                      // Convert to array and calculate averages
                      const manufacturerList = Object.values(manufacturerMap).map((mfg) => ({
                        ...mfg,
                        percentage: mfg.count > 0 ? mfg.percentage / mfg.count : 0,
                      }));

                      return {
                        position,
                        manufacturers: manufacturerList.sort((a, b) => b.marginValue - a.marginValue),
                      };
                    });

                  // Filter for Item margin type and group by position
                  const itemData = marginData.filter((item) => item.marginType === "Item");
                  
                  // Group by position for items
                  const groupedByPositionItems: Record<string, typeof itemData> = {};
                  itemData.forEach((item) => {
                    const position = item.position || "Unknown";
                    if (!groupedByPositionItems[position]) {
                      groupedByPositionItems[position] = [];
                    }
                    groupedByPositionItems[position].push(item);
                  });

                  // Prepare chart data - group products by position
                  const itemChartData = Object.keys(groupedByPositionItems)
                    .sort((a, b) => {
                      // Sort: Top first, then Bottom
                      if (a === "Top") return -1;
                      if (b === "Top") return 1;
                      return a.localeCompare(b);
                    })
                    .map((position) => {
                      const products = groupedByPositionItems[position];
                      // Group by product to aggregate values
                      const productMap: Record<string, {
                        productName: string;
                        productCode: string;
                        marginValue: number;
                        percentage: number;
                        position: string;
                        count: number;
                      }> = {};

                      products.forEach((item) => {
                        const key = `${item.productCode}`;
                        if (!productMap[key]) {
                          productMap[key] = {
                            productName: item.productName,
                            productCode: item.productCode,
                            marginValue: 0,
                            percentage: 0,
                            position: item.position,
                            count: 0,
                          };
                        }
                        productMap[key].marginValue += item.marginValue;
                        productMap[key].percentage += item.percentage;
                        productMap[key].count += 1;
                      });

                      // Convert to array and calculate averages
                      const productList = Object.values(productMap).map((prod) => ({
                        ...prod,
                        percentage: prod.count > 0 ? prod.percentage / prod.count : 0,
                      }));

                      return {
                        position,
                        products: productList.sort((a, b) => b.marginValue - a.marginValue),
                      };
                    });

                  return (
                    <>
                      {/* Summary Cards */}
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <StatCard
                          title="Gross Margin %"
                          value={marginSummaryData ? `${marginSummaryData.grossMarginPercentage.toFixed(2)}%` : "0.00%"}
                          icon={Percent}
                          variant="cyan"
                          trend={{ 
                            value: marginSummaryData 
                              ? `${marginSummaryData.grossMarginGrowthPercentage >= 0 ? '+' : ''}${marginSummaryData.grossMarginGrowthPercentage.toFixed(2)}%` 
                              : "0.00%", 
                            positive: marginSummaryData ? marginSummaryData.grossMarginGrowthPercentage >= 0 : true 
                          }}
                          className="animate-fade-up stagger-1"
                        />
                        <StatCard
                          title="Net Margin %"
                          value={marginSummaryData ? `${marginSummaryData.netMarginPercentage.toFixed(2)}%` : "0.00%"}
                          icon={TrendingUp}
                          variant="emerald"
                          trend={{ 
                            value: marginSummaryData 
                              ? `${marginSummaryData.netMarginGrowthPercentage >= 0 ? '+' : ''}${marginSummaryData.netMarginGrowthPercentage.toFixed(2)}%` 
                              : "0.00%", 
                            positive: marginSummaryData ? marginSummaryData.netMarginGrowthPercentage >= 0 : true 
                          }}
                          className="animate-fade-up stagger-2"
                        />
                        <StatCard
                          title="Total Profit (₹)"
                          value={formatCompact(marginSummaryData?.totalProfit ?? 0)}
                          icon={IndianRupee}
                          variant="amber"
                          trend={{ 
                            value: marginSummaryData 
                              ? `${marginSummaryData.totalProfitGrowthPercentage >= 0 ? '+' : ''}${marginSummaryData.totalProfitGrowthPercentage.toFixed(2)}%` 
                              : "0.00%", 
                            positive: marginSummaryData ? marginSummaryData.totalProfitGrowthPercentage >= 0 : true 
                          }}
                          className="animate-fade-up stagger-3"
                        />
                        <StatCard
                          title="Inventory Turns"
                          value={marginSummaryData ? marginSummaryData.inventoryTurns.toFixed(2) : "0.00"}
                          icon={RefreshCw}
                          variant="violet"
                          trend={{ 
                            value: marginSummaryData 
                              ? `${marginSummaryData.inventoryTurnsGrowthPercentage >= 0 ? '+' : ''}${marginSummaryData.inventoryTurnsGrowthPercentage.toFixed(2)}%` 
                              : "0.00%", 
                            positive: marginSummaryData ? marginSummaryData.inventoryTurnsGrowthPercentage >= 0 : true 
                          }}
                          className="animate-fade-up stagger-4"
                        />
                      </div>

                      {/* Margin Trend and Credit Aging Charts */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                        {/* Margin Trend Chart */}
                        <div className="animate-fade-up">
                          <ChartCard
                            title="Margin Trend (12 Months)"
                            subtitle="Gross Margin vs Net Margin"
                            variant="blue"
                            icon={<LineChartIcon className="h-4 w-4 text-blue-400" />}
                            showGradientLine={false}
                          >
                            <div className="h-80">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={marginTrends} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                  <XAxis 
                                    dataKey="month" 
                                    stroke="hsl(var(--muted-foreground))"
                                    fontSize={12}
                                  />
                                  <YAxis 
                                    stroke="hsl(var(--muted-foreground))"
                                    fontSize={12}
                                    tickFormatter={(value) => formatCurrency(value)}
                                    domain={yAxisDomain}
                                  />
                                  <Tooltip 
                                    content={({ active, payload, label }: any) => {
                                      if (!active || !payload || !payload.length) return null;
                                      
                                      const data = payload[0].payload;
                                      // Find the index of the current month in marginTrends
                                      const currentMonthIndex = marginTrends.findIndex((item: any) => item.month === label);
                                      const isLastActualMonth = currentMonthIndex === lastActualMonthIndex;
                                      
                                      // Find entries for actual and forecast
                                      const grossMarginActualEntry = payload.find((p: any) => p.dataKey === 'grossMarginActual');
                                      const grossMarginForecastEntry = isLastActualMonth ? null : payload.find((p: any) => p.dataKey === 'grossMarginForecast');
                                      const netMarginActualEntry = payload.find((p: any) => p.dataKey === 'netMarginActual');
                                      const netMarginForecastEntry = isLastActualMonth ? null : payload.find((p: any) => p.dataKey === 'netMarginForecast');
                                      
                                      // Use actual if available, otherwise forecast
                                      const grossMarginEntry = grossMarginActualEntry || grossMarginForecastEntry;
                                      const netMarginEntry = netMarginActualEntry || netMarginForecastEntry;
                                      
                                        return (
                                          <div className="chart-tooltip min-w-[200px] backdrop-blur-xl border border-white/20 shadow-2xl">
                                            {/* Header with month name */}
                                            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/10">
                                              <CalendarDays className="h-3 w-3 text-muted-foreground" />
                                              <p className="text-xs font-semibold text-foreground">
                                                {label}
                                              </p>
                                            </div>
                                            
                                            {/* Content grid */}
                                            <div className="space-y-1.5">
                                              {/* Gross Margin Actual */}
                                              {grossMarginActualEntry && (
                                                <div className="flex items-center justify-between gap-3">
                                                  <div className="flex items-center gap-1.5">
                                                    <div 
                                                      className="w-2 h-2 rounded-full flex-shrink-0"
                                                      style={{ 
                                                        backgroundColor: 'hsl(var(--chart-1))',
                                                        boxShadow: '0 0 6px hsl(var(--chart-1) / 0.4)'
                                                      }}
                                                    />
                                                    <span className="text-xs text-muted-foreground">Gross Margin</span>
                                                  </div>
                                                  <span 
                                                    className="text-xs font-semibold"
                                                    style={{ color: 'hsl(var(--chart-1))' }}
                                                  >
                                                    {formatCurrency(grossMarginActualEntry.value)}
                                                  </span>
                                                </div>
                                              )}
                                              
                                              {/* Gross Margin Forecast */}
                                              {grossMarginForecastEntry && (
                                                <div className="flex items-center justify-between gap-3">
                                                  <div className="flex items-center gap-1.5">
                                                    <div 
                                                      className="w-2 h-2 rounded-full flex-shrink-0"
                                                      style={{ 
                                                        backgroundColor: '#f97316',
                                                        boxShadow: '0 0 6px rgba(249, 115, 22, 0.4)'
                                                      }}
                                                    />
                                                    <span className="text-xs text-muted-foreground">Gross Margin (Forecast)</span>
                                                  </div>
                                                  <span 
                                                    className="text-xs font-semibold"
                                                    style={{ color: '#f97316' }}
                                                  >
                                                    {formatCurrency(grossMarginForecastEntry.value)}
                                                  </span>
                                                </div>
                                              )}
                                              
                                              {/* Net Margin Actual */}
                                              {netMarginActualEntry && (
                                                <div className="flex items-center justify-between gap-3">
                                                  <div className="flex items-center gap-1.5">
                                                    <div 
                                                      className="w-2 h-2 rounded-full flex-shrink-0"
                                                      style={{ 
                                                        backgroundColor: 'hsl(var(--chart-3))',
                                                        boxShadow: '0 0 6px hsl(var(--chart-3) / 0.4)'
                                                      }}
                                                    />
                                                    <span className="text-xs text-muted-foreground">Net Margin</span>
                                                  </div>
                                                  <span 
                                                    className="text-xs font-semibold"
                                                    style={{ color: 'hsl(var(--chart-3))' }}
                                                  >
                                                    {formatCurrency(netMarginActualEntry.value)}
                                                  </span>
                                                </div>
                                              )}
                                              
                                              {/* Net Margin Forecast */}
                                              {netMarginForecastEntry && (
                                                <div className="flex items-center justify-between gap-3">
                                                  <div className="flex items-center gap-1.5">
                                                    <div 
                                                      className="w-2 h-2 rounded-full flex-shrink-0"
                                                      style={{ 
                                                        backgroundColor: '#f97316',
                                                        boxShadow: '0 0 6px rgba(249, 115, 22, 0.4)'
                                                      }}
                                                    />
                                                    <span className="text-xs text-muted-foreground">Net Margin (Forecast)</span>
                                                  </div>
                                                  <span 
                                                    className="text-xs font-semibold"
                                                    style={{ color: '#f97316' }}
                                                  >
                                                    {formatCurrency(netMarginForecastEntry.value)}
                                                  </span>
                                                </div>
                                              )}
                                              
                                              {/* Difference indicator */}
                                              {grossMarginEntry && netMarginEntry && (
                                                <div className="pt-1.5 mt-1.5 border-t border-white/10">
                                                  <div className="flex items-center justify-between gap-3">
                                                    <div className="flex items-center gap-1.5">
                                                      <TrendingDown className="h-3 w-3 text-muted-foreground" />
                                                      <span className="text-xs text-muted-foreground">Difference</span>
                                                    </div>
                                                    <span className="text-xs font-medium text-muted-foreground">
                                                      {formatCurrency(grossMarginEntry.value - netMarginEntry.value)}
                                                    </span>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                    }}
                                  />
                                  <Legend 
                                    content={({ payload }: any) => {
                                      if (!payload || !payload.length) return null;
                                      
                                      return (
                                        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-2">
                                          {payload.map((entry: any, index: number) => {
                                            const getColor = (entry: any) => {
                                              if (entry.dataKey === 'grossMarginActual') return 'hsl(var(--chart-1))';
                                              if (entry.dataKey === 'grossMarginForecast') return '#f97316';
                                              if (entry.dataKey === 'netMarginActual') return 'hsl(var(--chart-3))';
                                              if (entry.dataKey === 'netMarginForecast') return '#f97316';
                                              return entry.color || entry.stroke;
                                            };
                                            
                                            const isDashed = entry.dataKey === 'grossMarginForecast' || entry.dataKey === 'netMarginForecast';
                                            
                                            return (
                                              <div 
                                                key={index} 
                                                className="flex items-center gap-1.5 px-2 py-0.5"
                                              >
                                                {isDashed ? (
                                                  <svg width="16" height="2" className="flex-shrink-0">
                                                    <line 
                                                      x1="0" 
                                                      y1="1" 
                                                      x2="16" 
                                                      y2="1" 
                                                      stroke={getColor(entry)} 
                                                      strokeWidth="2" 
                                                      strokeDasharray="4 2"
                                                    />
                                                  </svg>
                                                ) : (
                                                  <span 
                                                    className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                                                    style={{ backgroundColor: getColor(entry) }}
                                                  />
                                                )}
                                                <span className="text-xs text-muted-foreground whitespace-nowrap">{entry.value}</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    }}
                                  />
                                  {/* Gross Margin Actual */}
                                  <Line 
                                    type="monotone" 
                                    dataKey="grossMarginActual" 
                                    name="Gross Margin"
                                    stroke="hsl(var(--chart-1))" 
                                    strokeWidth={2}
                                    dot={{ fill: 'hsl(var(--chart-1))', strokeWidth: 2, r: 4 }}
                                    activeDot={{ r: 6, strokeWidth: 0 }}
                                    connectNulls={false}
                                  />
                                  {/* Gross Margin Forecast */}
                                  <Line 
                                    type="monotone" 
                                    dataKey="grossMarginForecast" 
                                    name="Gross Margin (Forecast)"
                                    stroke="#f97316" 
                                    strokeWidth={2}
                                    strokeDasharray="6 4"
                                    dot={{ fill: '#f97316', strokeWidth: 2, r: 4 }}
                                    activeDot={{ r: 6, strokeWidth: 0 }}
                                    connectNulls={false}
                                  />
                                  {/* Net Margin Actual */}
                                  <Line 
                                    type="monotone" 
                                    dataKey="netMarginActual" 
                                    name="Net Margin"
                                    stroke="hsl(var(--chart-3))" 
                                    strokeWidth={2}
                                    dot={{ fill: 'hsl(var(--chart-3))', strokeWidth: 2, r: 4 }}
                                    activeDot={{ r: 6, strokeWidth: 0 }}
                                    connectNulls={false}
                                  />
                                  {/* Net Margin Forecast */}
                                  <Line 
                                    type="monotone" 
                                    dataKey="netMarginForecast" 
                                    name="Net Margin (Forecast)"
                                    stroke="#f97316" 
                                    strokeWidth={2}
                                    strokeDasharray="6 4"
                                    dot={{ fill: '#f97316', strokeWidth: 2, r: 4 }}
                                    activeDot={{ r: 6, strokeWidth: 0 }}
                                    connectNulls={false}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          </ChartCard>
                        </div>

                        {/* Credit Aging Chart */}
                        <div className="animate-fade-up" style={{ animationDelay: '100ms' }}>
                          <ChartCard
                            title="Credit Aging vs Margin"
                            subtitle="Outstanding credit by aging period"
                            variant="emerald"
                            icon={<BarChart3 className="h-4 w-4 text-emerald-400" />}
                            showGradientLine={false}
                          >
                            {isCreditAgingLoading ? (
                              <div className="h-64 flex items-center justify-center">
                                <p className="text-sm text-muted-foreground">Loading credit aging data...</p>
                              </div>
                            ) : isCreditAgingError ? (
                              <div className="h-64 flex items-center justify-center">
                                <p className="text-sm text-destructive">Error loading credit aging data</p>
                              </div>
                            ) : (
                              <>
                                <div className="h-64">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={creditAgingData} margin={{ top: 5, right: 50, left: 60, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                    <XAxis 
                                      dataKey="bucket" 
                                      stroke="hsl(var(--muted-foreground))"
                                      fontSize={11}
                                    />
                                    <YAxis 
                                      yAxisId="left"
                                      stroke="hsl(var(--muted-foreground))"
                                      fontSize={12}
                                      tickFormatter={leftYAxisFormatter}
                                      width={60}
                                    />
                                    <YAxis 
                                      yAxisId="right"
                                      orientation="right"
                                      stroke="hsl(var(--muted-foreground))"
                                      fontSize={12}
                                      tickFormatter={(value) => `${value.toFixed(1)}%`}
                                    />
                                    <Tooltip 
                                      content={({ active, payload }: any) => {
                                        if (!active || !payload || !payload.length) return null;
                                        
                                        const data = payload[0].payload;
                                        const index = creditAgingData.findIndex(item => item.bucket === data.bucket);
                                        const barColor = index >= 0 ? COLORS[index] : COLORS[0];
                                        
                                        return (
                                          <div className="chart-tooltip min-w-[200px] backdrop-blur-xl border border-white/20 shadow-2xl">
                                            {/* Header with period name */}
                                            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/10">
                                              <div 
                                                className="w-2.5 h-2.5 rounded-full flex-shrink-0 shadow-sm"
                                                style={{ 
                                                  backgroundColor: barColor,
                                                  boxShadow: `0 0 8px ${barColor}40`
                                                }}
                                              />
                                              <p className="text-xs font-semibold text-foreground">
                                                {data.bucket}
                                              </p>
                                            </div>
                                            
                                            {/* Content grid */}
                                            <div className="space-y-1.5">
                                              {/* Outstanding Amount */}
                                              <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-1.5">
                                                  <Wallet className="h-3 w-3 text-muted-foreground" />
                                                  <span className="text-xs text-muted-foreground">Outstanding</span>
                                                </div>
                                                <span className="text-xs font-semibold text-foreground">
                                                  {formatCurrency(data.outstanding)}
                                                </span>
                                              </div>
                                              
                                              {/* Margin Percentage */}
                                              <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-1.5">
                                                  <Percent className="h-3 w-3 text-muted-foreground" />
                                                  <span className="text-xs text-muted-foreground">Margin</span>
                                                </div>
                                                <span 
                                                  className="text-xs font-semibold"
                                                  style={{ color: barColor }}
                                                >
                                                  {data.margin.toFixed(1)}%
                                                </span>
                                              </div>
                                              
                                              {/* Customer Count */}
                                              <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-1.5">
                                                  <Users className="h-3 w-3 text-muted-foreground" />
                                                  <span className="text-xs text-muted-foreground">Customers</span>
                                                </div>
                                                <span className="text-xs font-semibold text-foreground">
                                                  {data.customers.toLocaleString()}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }}
                                    />
                                    <Bar yAxisId="left" dataKey="outstanding" radius={[4, 4, 0, 0]}>
                                      {creditAgingData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                                      ))}
                                    </Bar>
                                    <Line 
                                      yAxisId="right"
                                      type="monotone" 
                                      dataKey="margin" 
                                      stroke="#f97316" 
                                      strokeWidth={2}
                                      dot={{ fill: '#f97316', strokeWidth: 2, r: 4 }}
                                      activeDot={{ r: 6, strokeWidth: 0 }}
                                      connectNulls={false}
                                    />
                                  </ComposedChart>
                                </ResponsiveContainer>
                              </div>
                              
                              {/* Summary cards */}
                              <div className="grid grid-cols-4 gap-3 mt-4">
                                {creditAgingData.map((item, index) => (
                                  <div 
                                    key={item.bucket}
                                    className="text-center p-3 rounded-lg"
                                    style={{ backgroundColor: `${COLORS[index]}10` }}
                                  >
                                    <p className="text-xs text-muted-foreground">{item.bucket}</p>
                                    <p className="font-semibold text-sm" style={{ color: COLORS[index] }}>
                                      {item.margin}% margin
                                    </p>
                                    <p className="text-xs text-muted-foreground">{item.customers} customers</p>
                                  </div>
                                ))}
                              </div>
                            </>
                            )}
                          </ChartCard>
                        </div>
                      </div>

                      {/* Manufacturer Margins and Top Customers Tables */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mt-6">
                        {/* Manufacturer Margins Table */}
                        <div className="animate-fade-up" style={{ animationDelay: '200ms' }}>
                          <ChartCard
                            title="Manufacturer Margins"
                            variant="purple"
                            icon={<Package className="h-4 w-4 text-purple-400" />}
                            showGradientLine={false}
                          >
                            <div className="space-y-6">
                              {/* Top Position Table */}
                              {(() => {
                                const topData = manufacturerMarginsData?.filter((item: any) => item.position === 'Top') || [];
                                const topTransformed = transformManufacturerMargins(topData);
                                
                                return (
                                  <div>
                                    <h4 className="text-sm font-semibold text-foreground mb-3 px-2">Top manufacturers by value</h4>
                                    <div className="max-h-[400px] overflow-y-auto">
                                      <table className="w-full table-fixed">
                                        <colgroup>
                                          <col className="w-[30%]" />
                                          <col className="w-[18%]" />
                                          <col className="w-[13%]" />
                                          <col className="w-[13%]" />
                                          <col className="w-[15%]" />
                                          <col className="w-[13%]" />
                                        </colgroup>
                                        <thead>
                                          <tr className="bg-muted">
                                            <th className="sticky top-0 bg-muted z-10 text-left px-2 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Manufacturer</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-2 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">value</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-2 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Gross %</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-2 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Net %</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-2 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Scheme %</th>
                                            <th className="sticky top-0 bg-muted z-10 text-center px-2 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Trend</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/50">
                                          {isManufacturerMarginsLoading ? (
                                            <tr>
                                              <td colSpan={6} className="px-2 py-8 text-center text-sm text-muted-foreground">
                                                Loading manufacturer margins...
                                              </td>
                                            </tr>
                                          ) : isManufacturerMarginsError ? (
                                            <tr>
                                              <td colSpan={6} className="px-2 py-8 text-center text-sm text-destructive">
                                                Error loading manufacturer margins
                                              </td>
                                            </tr>
                                          ) : topTransformed.length === 0 ? (
                                            <tr>
                                              <td colSpan={6} className="px-2 py-8 text-center text-sm text-muted-foreground">
                                                No top manufacturer margins data available
                                              </td>
                                            </tr>
                                          ) : (
                                            topTransformed.map((mfr, index) => (
                                              <tr 
                                                key={mfr.id}
                                                className="animate-fade-up hover:bg-muted/50 transition-colors"
                                                style={{ animationDelay: `${index * 50}ms` }}
                                              >
                                                <td className="px-2 py-2 text-[11px] sm:text-sm font-medium truncate" title={mfr.name}>{mfr.name}</td>
                                                <td className="px-2 py-2 text-[11px] sm:text-sm text-right font-mono text-muted-foreground">
                                                  {formatCurrency(mfr.volume)}
                                                </td>
                                                <td className="px-2 py-2 text-[11px] sm:text-sm text-right">
                                                  <span className={cn(
                                                    'font-semibold',
                                                    mfr.grossMargin >= 9 ? 'text-green-600 dark:text-green-400' : 
                                                    mfr.grossMargin >= 8 ? 'text-foreground' : 'text-orange-600 dark:text-orange-400'
                                                  )}>
                                                    {mfr.grossMargin.toFixed(1)}%
                                                  </span>
                                                </td>
                                                <td className="px-2 py-2 text-[11px] sm:text-sm text-right">
                                                  <span className={cn(
                                                    'font-semibold',
                                                    mfr.netMargin >= 6 ? 'text-green-600 dark:text-green-400' : 
                                                    mfr.netMargin >= 5 ? 'text-foreground' : 'text-orange-600 dark:text-orange-400'
                                                  )}>
                                                    {mfr.netMargin.toFixed(1)}%
                                                  </span>
                                                </td>
                                                <td className="px-2 py-2 text-[11px] sm:text-sm text-right text-muted-foreground">
                                                  {mfr.schemeBenefit.toFixed(1)}%
                                                </td>
                                                <td className="px-2 py-2 text-center">
                                                  {mfr.trend === 'UP' && (
                                                    <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600 dark:text-green-400 inline-block" />
                                                  )}
                                                  {mfr.trend === 'Down' && (
                                                    <TrendingDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600 dark:text-red-400 inline-block" />
                                                  )}
                                                  {mfr.trend === 'stable' && (
                                                    <Minus className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground inline-block" />
                                                  )}
                                                </td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Bottom Position Table */}
                              {(() => {
                                const bottomData = manufacturerMarginsData?.filter((item: any) => item.position === 'Bottom') || [];
                                const bottomTransformed = transformManufacturerMargins(bottomData);
                                
                                return (
                                  <div>
                                    <h4 className="text-sm font-semibold text-foreground mb-3 px-2">Bottom manufacturers by value</h4>
                                    <div className="max-h-[400px] overflow-y-auto">
                                      <table className="w-full table-fixed">
                                        <colgroup>
                                          <col className="w-[30%]" />
                                          <col className="w-[18%]" />
                                          <col className="w-[13%]" />
                                          <col className="w-[13%]" />
                                          <col className="w-[15%]" />
                                          <col className="w-[13%]" />
                                        </colgroup>
                                        <thead>
                                          <tr className="bg-muted">
                                            <th className="sticky top-0 bg-muted z-10 text-left px-2 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Manufacturer</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-2 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">value</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-2 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Gross %</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-2 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Net %</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-2 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Scheme %</th>
                                            <th className="sticky top-0 bg-muted z-10 text-center px-2 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Trend</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/50">
                                          {isManufacturerMarginsLoading ? (
                                            <tr>
                                              <td colSpan={6} className="px-2 py-8 text-center text-sm text-muted-foreground">
                                                Loading manufacturer margins...
                                              </td>
                                            </tr>
                                          ) : isManufacturerMarginsError ? (
                                            <tr>
                                              <td colSpan={6} className="px-2 py-8 text-center text-sm text-destructive">
                                                Error loading manufacturer margins
                                              </td>
                                            </tr>
                                          ) : bottomTransformed.length === 0 ? (
                                            <tr>
                                              <td colSpan={6} className="px-2 py-8 text-center text-sm text-muted-foreground">
                                                No bottom manufacturer margins data available
                                              </td>
                                            </tr>
                                          ) : (
                                            bottomTransformed.map((mfr, index) => (
                                              <tr 
                                                key={mfr.id}
                                                className="animate-fade-up hover:bg-muted/50 transition-colors"
                                                style={{ animationDelay: `${index * 50}ms` }}
                                              >
                                                <td className="px-2 py-2 text-[11px] sm:text-sm font-medium truncate" title={mfr.name}>{mfr.name}</td>
                                                <td className="px-2 py-2 text-[11px] sm:text-sm text-right font-mono text-muted-foreground">
                                                  {formatCurrency(mfr.volume)}
                                                </td>
                                                <td className="px-2 py-2 text-[11px] sm:text-sm text-right">
                                                  <span className={cn(
                                                    'font-semibold',
                                                    mfr.grossMargin >= 9 ? 'text-green-600 dark:text-green-400' : 
                                                    mfr.grossMargin >= 8 ? 'text-foreground' : 'text-orange-600 dark:text-orange-400'
                                                  )}>
                                                    {mfr.grossMargin.toFixed(1)}%
                                                  </span>
                                                </td>
                                                <td className="px-2 py-2 text-[11px] sm:text-sm text-right">
                                                  <span className={cn(
                                                    'font-semibold',
                                                    mfr.netMargin >= 6 ? 'text-green-600 dark:text-green-400' : 
                                                    mfr.netMargin >= 5 ? 'text-foreground' : 'text-orange-600 dark:text-orange-400'
                                                  )}>
                                                    {mfr.netMargin.toFixed(1)}%
                                                  </span>
                                                </td>
                                                <td className="px-2 py-2 text-[11px] sm:text-sm text-right text-muted-foreground">
                                                  {mfr.schemeBenefit.toFixed(1)}%
                                                </td>
                                                <td className="px-2 py-2 text-center">
                                                  {mfr.trend === 'UP' && (
                                                    <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600 dark:text-green-400 inline-block" />
                                                  )}
                                                  {mfr.trend === 'Down' && (
                                                    <TrendingDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600 dark:text-red-400 inline-block" />
                                                  )}
                                                  {mfr.trend === 'stable' && (
                                                    <Minus className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground inline-block" />
                                                  )}
                                                </td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </ChartCard>
                        </div>

                        {/* Top Customers by Profit Table */}
                        <div className="animate-fade-up" style={{ animationDelay: '300ms' }}>
                          <ChartCard
                            title="Top Customers by Profit"
                            variant="emerald"
                            icon={<Users className="h-4 w-4 text-emerald-400" />}
                            showGradientLine={false}
                          >
                            <div className="space-y-6">
                              {/* Top Position Table */}
                              {(() => {
                                const topData = customerMarginsData?.filter((item: any) => item.position === 'Top') || [];
                                const topTransformed = mapCustomerMargins(topData);
                                
                                return (
                                  <div>
                                    <h4 className="text-sm font-semibold text-foreground mb-3 px-2">Top customers by profit</h4>
                                    <div className="max-h-[400px] overflow-y-auto">
                                      <table className="w-full table-fixed">
                                        <colgroup>
                                          <col className="w-[25%]" />
                                          <col className="w-[18%]" />
                                          <col className="w-[16%]" />
                                          <col className="w-[12%]" />
                                          <col className="w-[13%]" />
                                          <col className="w-[15%]" />
                                        </colgroup>
                                        <thead>
                                          <tr className="bg-muted">
                                            <th className="sticky top-0 bg-muted z-10 text-left px-3 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customer</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-3 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sales</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-3 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Profit</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-3 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Margin</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-3 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Cr Days</th>
                                            <th className="sticky top-0 bg-muted z-10 text-center px-3 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/50">
                                          {isCustomerMarginsLoading ? (
                                            <tr>
                                              <td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">
                                                Loading customer data...
                                              </td>
                                            </tr>
                                          ) : isCustomerMarginsError ? (
                                            <tr>
                                              <td colSpan={6} className="px-3 py-8 text-center text-sm text-red-600 dark:text-red-400">
                                                Error loading customer data
                                              </td>
                                            </tr>
                                          ) : topTransformed.length === 0 ? (
                                            <tr>
                                              <td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">
                                                No top customer data available
                                              </td>
                                            </tr>
                                          ) : (
                                            topTransformed.map((customer, index) => (
                                              <tr 
                                                key={customer.id}
                                                className="animate-fade-up hover:bg-muted/50 transition-colors"
                                                style={{ animationDelay: `${index * 50}ms` }}
                                              >
                                                <td className="px-3 py-2 text-[11px] sm:text-sm font-medium truncate" title={customer.name}>{customer.name}</td>
                                                <td className="px-3 py-2 text-[11px] sm:text-sm text-right font-mono text-muted-foreground">
                                                  {formatCurrency(customer.sales)}
                                                </td>
                                                <td className="px-3 py-2 text-[11px] sm:text-sm text-right font-mono font-semibold text-green-600 dark:text-green-400">
                                                  {formatCurrency(customer.profit)}
                                                </td>
                                                <td className="px-3 py-2 text-[11px] sm:text-sm text-right">
                                                  <span className={cn(
                                                    'font-semibold',
                                                    customer.margin >= 7 ? 'text-green-600 dark:text-green-400' : 
                                                    customer.margin >= 5 ? 'text-foreground' : 'text-orange-600 dark:text-orange-400'
                                                  )}>
                                                    {customer.margin.toFixed(1)}%
                                                  </span>
                                                </td>
                                                <td className="px-3 py-2 text-[11px] sm:text-sm text-right">
                                                  <span className={cn(
                                                    customer.creditDays <= 21 ? 'text-green-600 dark:text-green-400' :
                                                    customer.creditDays <= 45 ? 'text-orange-600 dark:text-orange-400' : 'text-red-600 dark:text-red-400'
                                                  )}>
                                                    {customer.creditDays}
                                                  </span>
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                  <Badge 
                                                    variant="outline"
                                                    className={cn(
                                                      'text-[10px] sm:text-xs px-1.5 py-0.5',
                                                      customer.status === 'good' && 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
                                                      customer.status === 'warning' && 'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800',
                                                      customer.status === 'critical' && 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
                                                    )}
                                                  >
                                                    {customer.status}
                                                  </Badge>
                                                </td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Bottom Position Table */}
                              {(() => {
                                const bottomData = customerMarginsData?.filter((item: any) => item.position === 'Bottom') || [];
                                const bottomTransformed = mapCustomerMargins(bottomData);
                                
                                return (
                                  <div>
                                    <h4 className="text-sm font-semibold text-foreground mb-3 px-2">Bottom customers by profit</h4>
                                    <div className="max-h-[400px] overflow-y-auto">
                                      <table className="w-full table-fixed">
                                        <colgroup>
                                          <col className="w-[25%]" />
                                          <col className="w-[18%]" />
                                          <col className="w-[16%]" />
                                          <col className="w-[12%]" />
                                          <col className="w-[13%]" />
                                          <col className="w-[15%]" />
                                        </colgroup>
                                        <thead>
                                          <tr className="bg-muted">
                                            <th className="sticky top-0 bg-muted z-10 text-left px-3 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Customer</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-3 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sales</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-3 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Profit</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-3 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Margin</th>
                                            <th className="sticky top-0 bg-muted z-10 text-right px-3 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Cr Days</th>
                                            <th className="sticky top-0 bg-muted z-10 text-center px-3 py-2 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/50">
                                          {isCustomerMarginsLoading ? (
                                            <tr>
                                              <td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">
                                                Loading customer data...
                                              </td>
                                            </tr>
                                          ) : isCustomerMarginsError ? (
                                            <tr>
                                              <td colSpan={6} className="px-3 py-8 text-center text-sm text-red-600 dark:text-red-400">
                                                Error loading customer data
                                              </td>
                                            </tr>
                                          ) : bottomTransformed.length === 0 ? (
                                            <tr>
                                              <td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">
                                                No bottom customer data available
                                              </td>
                                            </tr>
                                          ) : (
                                            bottomTransformed.map((customer, index) => (
                                              <tr 
                                                key={customer.id}
                                                className="animate-fade-up hover:bg-muted/50 transition-colors"
                                                style={{ animationDelay: `${index * 50}ms` }}
                                              >
                                                <td className="px-3 py-2 text-[11px] sm:text-sm font-medium truncate" title={customer.name}>{customer.name}</td>
                                                <td className="px-3 py-2 text-[11px] sm:text-sm text-right font-mono text-muted-foreground">
                                                  {formatCurrency(customer.sales)}
                                                </td>
                                                <td className="px-3 py-2 text-[11px] sm:text-sm text-right font-mono font-semibold text-green-600 dark:text-green-400">
                                                  {formatCurrency(customer.profit)}
                                                </td>
                                                <td className="px-3 py-2 text-[11px] sm:text-sm text-right">
                                                  <span className={cn(
                                                    'font-semibold',
                                                    customer.margin >= 7 ? 'text-green-600 dark:text-green-400' : 
                                                    customer.margin >= 5 ? 'text-foreground' : 'text-orange-600 dark:text-orange-400'
                                                  )}>
                                                    {customer.margin.toFixed(1)}%
                                                  </span>
                                                </td>
                                                <td className="px-3 py-2 text-[11px] sm:text-sm text-right">
                                                  <span className={cn(
                                                    customer.creditDays <= 21 ? 'text-green-600 dark:text-green-400' :
                                                    customer.creditDays <= 45 ? 'text-orange-600 dark:text-orange-400' : 'text-red-600 dark:text-red-400'
                                                  )}>
                                                    {customer.creditDays}
                                                  </span>
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                  <Badge 
                                                    variant="outline"
                                                    className={cn(
                                                      'text-[10px] sm:text-xs px-1.5 py-0.5',
                                                      customer.status === 'good' && 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
                                                      customer.status === 'warning' && 'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800',
                                                      customer.status === 'critical' && 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
                                                    )}
                                                  >
                                                    {customer.status}
                                                  </Badge>
                                                </td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </ChartCard>
                        </div>
                      </div>

                    </>
                  );
                })()}
              </>
            )}
          </TabsContent>
          
          {/* Salesman Analysis Tab */}
          <TabsContent value="salesman" className="space-y-2">
            {/* Salesman Wise Margin Analysis Table */}
            <Card className="relative overflow-hidden rounded-xl glass-card noise-overlay border-white/10 dark:border-white/5 bg-gradient-to-br from-violet-500/10 to-purple-500/5">
              <CardHeader className="relative pb-2 pt-3 sm:pt-4 px-3 sm:px-4">
                <CardTitle className="text-sm sm:text-base font-semibold flex items-center gap-1.5 sm:gap-2">
                  <Percent className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-violet-500" />
                  <span className="truncate flex items-center gap-2">
                    <span>Salesman Wise Margin Analysis</span>
                    <span className="text-[10px] sm:text-xs font-semibold text-violet-700 dark:text-violet-300 bg-violet-50/90 dark:bg-violet-500/10 px-2 py-0.5 rounded-full border border-violet-200/60 dark:border-violet-500/30">
                      (Year to Date Overall)
                    </span>
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="relative px-2 sm:px-4 pb-3 sm:pb-4 pt-0">
                <div className="overflow-hidden rounded-lg border border-border/50">
                  <div className="overflow-x-auto -mx-2 sm:mx-0">
                  <table className="w-full min-w-[640px]">
                    <thead>
                      <tr className="bg-muted">
                        <th className="sticky top-0 bg-muted z-10 text-left px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">#</th>
                        <th className="sticky top-0 bg-muted z-10 text-left px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</th>
                        <th className="sticky top-0 bg-muted z-10 text-left px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Mobile</th>
                        <th className="sticky top-0 bg-muted z-10 text-right px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sale Value</th>
                        <th className="sticky top-0 bg-muted z-10 text-right px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Collection Value</th>
                        <th className="sticky top-0 bg-muted z-10 text-right px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Outstanding Value</th>
                        <th className="sticky top-0 bg-muted z-10 text-right px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Margin %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50">
                      {isSalesmanMarginLoading && (
                        <tr>
                          <td className="px-3 py-4 text-sm text-muted-foreground text-center" colSpan={7}>
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                              Loading...
                            </div>
                          </td>
                        </tr>
                      )}
                      {isSalesmanMarginError && !isSalesmanMarginLoading && (
                        <tr>
                          <td className="px-3 py-4 text-sm text-destructive text-center" colSpan={7}>Failed to load data</td>
                        </tr>
                      )}
                      {!isSalesmanMarginLoading && !isSalesmanMarginError && (salesmanMarginData || []).map((item, idx) => (
                        <tr 
                          key={idx} 
                          className="group hover:bg-gradient-to-r hover:from-violet-500/5 hover:to-transparent transition-colors duration-200"
                        >
                          <td className="px-2 sm:px-3 py-1 sm:py-1.5">
                            <span className="inline-flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-muted text-[10px] sm:text-xs font-medium">
                              {idx + 1}
                            </span>
                          </td>
                          <td className="px-2 sm:px-3 py-1 sm:py-1.5">
                            <span className="text-xs sm:text-sm font-medium text-foreground truncate">{item.salesManName}</span>
                          </td>
                          <td className="px-2 sm:px-3 py-1 sm:py-1.5 hidden sm:table-cell">
                            <span className="text-xs sm:text-sm text-muted-foreground font-mono">{item.mobileNo || "-"}</span>
                          </td>
                          <td className="px-2 sm:px-3 py-1 sm:py-1.5 text-right">
                            <span className="text-xs sm:text-sm font-semibold text-foreground">{formatINR(Number(item.saleValue))}</span>
                          </td>
                          <td className="px-2 sm:px-3 py-1 sm:py-1.5 text-right">
                            <span className="text-xs sm:text-sm font-semibold text-foreground">{formatINR(Number(item.collectionValue))}</span>
                          </td>
                          <td className="px-2 sm:px-3 py-1 sm:py-1.5 text-right">
                            <span className="text-xs sm:text-sm font-semibold text-foreground">{formatINR(Number(item.outstandingValue))}</span>
                          </td>
                          <td className="px-2 sm:px-3 py-1 sm:py-1.5 text-right">
                            <span className="inline-flex items-center justify-center min-w-[1.25rem] sm:min-w-[1.5rem] px-1 sm:px-1.5 py-0.5 rounded-full bg-violet-500/10 text-violet-600 dark:text-violet-400 text-[10px] sm:text-xs font-medium">
                              {Number(item.marginPercentage).toFixed(2)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Dashboard;

